/**
 * IA SCANNER - MEJORADO CON TAILWIND
 * Panel de análisis avanzado basado en Trade Control
 * Features claras y directas + análisis profundo
 */

// Helper function to identify Break Even trades
function isBreakEvenTrade(trade, balance) {
    const profitUSD = parseFloat(trade.profitUSD) || 0;
    const profitPercent = (profitUSD / balance) * 100;
    return profitPercent >= -0.1 && profitPercent <= 0.1;
}

class IAScannerSAAS {
    constructor() {
        this.modal = null;
        this.overlay = null;
        this.isOpen = false;
        this.init();
    }

    init() {
        this.createModal();
        this.setupEventListeners();
    }

    createModal() {
        if (this.modal) return;

        // Overlay
        this.overlay = document.createElement('div');
        this.overlay.id = 'iaScannerOverlay';
        this.overlay.className = 'fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] hidden transition-opacity duration-300';
        this.overlay.addEventListener('click', () => this.close());

        // Modal - CENTRADO Y RESPONSIVE
        this.modal = document.createElement('div');
        this.modal.id = 'iaScannerModal';
        this.modal.className = 'fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[95%] sm:w-full max-w-2xl max-h-[90vh] sm:max-h-[80vh] bg-gradient-to-b from-slate-950 to-slate-900 rounded-xl sm:rounded-2xl overflow-hidden z-[10000] hidden transition-all duration-300 opacity-0 scale-95 shadow-2xl border border-slate-700/50 flex flex-col min-h-0';
        this.modal.addEventListener('click', (e) => e.stopPropagation());

        document.body.appendChild(this.overlay);
        document.body.appendChild(this.modal);
    }

    setupEventListeners() {
        // Cerrar con ESC
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.isOpen) {
                this.close();
            }
        });
    }

    open() {
        if (window.demoGate && window.demoGate('ia_scanner', 'IA Scanner')) {
            return;
        }
        if (this.isOpen) return;
        this.isOpen = true;
        this.overlay.classList.remove('hidden');
        this.modal.classList.remove('hidden');
        
        // MOSTRAR LOADING SPINNER
        this.modal.innerHTML = `
            <div class="flex items-center justify-center min-h-[50vh] p-6">
                <div class="w-full max-w-md rounded-2xl border border-slate-700/60 bg-gradient-to-b from-slate-900/80 to-slate-950/80 shadow-2xl">
                    <div class="p-5 border-b border-slate-700/50">
                        <div class="flex items-center gap-3">
                            <div class="w-10 h-10 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center">
                                <div class="w-6 h-6 border-2 border-blue-400/60 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                            <div>
                                <p class="text-white font-bold text-base">IA Scanner</p>
                                <p class="text-slate-400 text-xs">Cargando histórico completo…</p>
                            </div>
                        </div>
                    </div>
                    <div class="p-5 space-y-4">
                        <div class="h-2 w-full rounded-full bg-slate-800/70 overflow-hidden">
                            <div class="h-full w-1/2 bg-gradient-to-r from-blue-500 via-cyan-400 to-blue-500 animate-pulse"></div>
                        </div>
                        <div class="space-y-2">
                            <div class="h-3 w-3/4 rounded bg-slate-800/70 animate-pulse"></div>
                            <div class="h-3 w-2/3 rounded bg-slate-800/70 animate-pulse"></div>
                            <div class="h-3 w-1/2 rounded bg-slate-800/70 animate-pulse"></div>
                        </div>
                        <div class="text-[11px] text-slate-500">
                            Analizando todos tus trades históricos (últimos 6 meses).
                        </div>
                    </div>
            </div>
        `;

        // Listeners are bound after render()
        
        setTimeout(() => {
            this.modal.classList.remove('opacity-0', 'scale-95');
            this.modal.classList.add('opacity-100', 'scale-100');
        }, 10);
        
        // CARGAR HISTÓRICO COMPLETO ANTES DE RENDERIZAR
        this.loadFullHistory().then(() => {
            setTimeout(() => {
                this.render();
            }, 800);
        });
    }

    async loadFullHistory() {
        if (typeof window.loadTradesFromDB === 'function') {
            try {
                const completeData = await window.loadTradesFromDB(window.currentAccount, null, true);
                window.iaScannerFullData = completeData;
            } catch (e) {
                // silently fail
            }
        }
    }

    close() {
        if (!this.isOpen) return;
        localStorage.setItem('iaScannerMinTrades', '2');
        this.modal.classList.add('opacity-0', 'scale-95');
        this.modal.classList.remove('opacity-100', 'scale-100');
        this.overlay.classList.add('hidden');
        setTimeout(() => {
            this.modal.classList.add('hidden');
            this.isOpen = false;
        }, 300);
    }

    /**
     * 🔧 LÓGICA SIMPLIFICADA Y CLARA
     * 1. Obtener TODOS los trades de la cuenta
     * 2. Filtrar por DÍA DE LA SEMANA ACTUAL (ej: Jueves)
     * 3. Calcular métricas SOLO de ese día
     * 4. Contexto: "Jueves - 20 de 58 trades (últimos 6 meses)"
     */
    getMetrics() {
        // Priorizar histórico completo si está disponible
        let journalData = window.iaScannerFullData;
        if (!journalData || Object.keys(journalData).length === 0) {
            journalData = window.journalData || {};
        }
        
        const currentAccountData = window.accounts?.[window.currentAccount];
        
        if (!currentAccountData) {
            return null;
        }

        // ✅ PASO 1: Recopilar TODOS los trades
        let allTrades = [];
        Object.keys(journalData).forEach(dateKey => {
            const trades = journalData[dateKey] || [];
            trades.forEach(t => {
                if (!t.date && !t.trade_date) {
                    t.date = dateKey;
                }
            });
            allTrades.push(...trades);
        });

        if (allTrades.length === 0) {
            return null;
        }

        // ✅ PASO 2: Determinar el DÍA ACTUAL (hoy)
        const now = new Date();
        const todayDayOfWeek = now.getDay(); // 0=domingo, 1=lunes, ..., 4=jueves, etc.
        const dayNameMap = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        const todayDayName = dayNameMap[todayDayOfWeek];
        
        // Formatear fecha de hoy para display
        const todayFormatted = now.toLocaleDateString('es-ES', { 
            year: 'numeric', 
            month: '2-digit', 
            day: '2-digit' 
        });

        const analysisMode = localStorage.getItem('iaScannerMode') || 'day';
        const minTradesRequired = parseInt(localStorage.getItem('iaScannerMinTrades') || '2', 10);

        // ✅ PASO 3: Filtrar trades del DÍA DE SEMANA ACTUAL de los últimos 6 meses
        const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
        const sixMonthsAgoStr = sixMonthsAgo.getFullYear() + '-' + 
                               String(sixMonthsAgo.getMonth() + 1).padStart(2, '0') + '-' +
                               String(sixMonthsAgo.getDate()).padStart(2, '0');
        
        // Filtrar: mismo día de semana + últimos 6 meses
        let dayTrades = allTrades.filter(t => {
            const tradeDate = t.date || t.trade_date || '';
            const tradeDateObj = new Date(tradeDate);
            const tradeDayOfWeek = tradeDateObj.getDay();
            const isWithin6Months = tradeDate >= sixMonthsAgoStr;
            return tradeDayOfWeek === todayDayOfWeek && isWithin6Months;
        });

        // 🔍 Contar trades del MES ACTUAL
        const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
        const currentYear = now.getFullYear();
        const currentMonthStr = `${currentYear}-${currentMonth}`;
        const currentMonthTrades = allTrades.filter(t => {
            const tradeDate = (t.date || t.trade_date || '').substring(0, 7);
            return tradeDate === currentMonthStr;
        });

        let analysisTrades = [];
        let effectiveMode = analysisMode;

        // ⚠️ Si el mes ACTUAL está vacío, cambiar automáticamente a "últimos 6 meses"
        if (currentMonthTrades.length === 0 && analysisMode === 'day') {
            // Caer a análisis de últimos 6 meses completos
            analysisTrades = allTrades.filter(t => {
                const tradeDate = t.date || t.trade_date || '';
                return tradeDate >= sixMonthsAgoStr;
            });
            effectiveMode = 'sixMonths'; // Marcar que cayó a 6 meses
        } else if (analysisMode === 'day') {
            if (dayTrades.length === 0) {
                // Si hay trades en el mes pero no del día, caer a 6 meses
                analysisTrades = allTrades.filter(t => {
                    const tradeDate = t.date || t.trade_date || '';
                    return tradeDate >= sixMonthsAgoStr;
                });
                effectiveMode = 'sixMonths';
            } else {
                analysisTrades = dayTrades;
            }
        } else if (analysisMode === '6m') {
            // ✅ Modo "Últimos 6 meses" - todos los trades de los últimos 6 meses
            analysisTrades = allTrades.filter(t => {
                const tradeDate = t.date || t.trade_date || '';
                return tradeDate >= sixMonthsAgoStr;
            });
        } else {
            // Fallback: últimos 6 meses
            analysisTrades = allTrades.filter(t => {
                const tradeDate = t.date || t.trade_date || '';
                return tradeDate >= sixMonthsAgoStr;
            });
        }

        // Mínimo de trades para análisis
        if (analysisTrades.length === 0 || analysisTrades.length < minTradesRequired) {
            return null;
        }

        // ✅ PASO 4: Calcular balance y ganancias
        const initialBalance = currentAccountData.initial_balance || currentAccountData.balance || 10000;
        const currentBalance = window.balanceStateManager 
            ? window.balanceStateManager.getAccountBalance(window.currentAccount)
            : this.calculateBalance(allTrades, initialBalance);
        const totalProfit = currentBalance - initialBalance;

        // ✅ PASO 5: Calcular TODAS las métricas usando analysisTrades (día actual)
        const winRate = this.calculateWinRate(analysisTrades, initialBalance);
        const tradeBreakdown = this.getTradeBreakdown(analysisTrades, initialBalance);
        const profitFactor = this.calculateProfitFactor(analysisTrades);
        const avgWin = this.calculateAvgWin(analysisTrades);
        const avgLoss = this.calculateAvgLoss(analysisTrades);
        const currentStreak = this.calculateStreak(analysisTrades, initialBalance);
        const bestPair = this.getBestPair(analysisTrades);
        const worstPair = this.getWorstPair(analysisTrades);
        const bestHour = this.findBestTradingHour(analysisTrades);
        const riskRewardRatio = this.calculateRiskRewardRatio(analysisTrades);

        // 📰 WinRate en días con noticias por impacto (solo mismo día de la semana)
        const newsImpactByDay = this.calculateNewsImpactByDay(dayTrades, todayDayName, initialBalance);
        
        // 📊 Ganancia/Pérdida del DÍA ACTUAL (solo del jueves, no total de cuenta)
        const dayProfit = analysisTrades.reduce((sum, t) => sum + (parseFloat(t.profitUSD) || 0), 0);
        const dayProfitPercent = (dayProfit / initialBalance) * 100;

        // 🎯 LABEL CLARO Y PRECISO - SIN REPETIR TRADES
        let periodLabel = '';
        if (effectiveMode === 'sixMonths' || analysisMode === '6m') {
            periodLabel = `Últimos 6 meses (${analysisTrades.length} trades)`;
        } else if (analysisMode === 'day') {
            periodLabel = `${todayDayName} - Históricos (${todayFormatted})`;
        } else {
            periodLabel = `Últimos 6 meses (${analysisTrades.length} trades)`;
        }

        return {
            // Contexto claro
            dayName: todayDayName,
            todayDate: todayFormatted,
            periodLabel: periodLabel,
            periodStart: sixMonthsAgoStr,  // 📅 Fecha de inicio del histórico
            analysisMode: analysisMode,
            effectiveMode: effectiveMode,  // 🔄 Modo actual después de fallback
            minTradesRequired: minTradesRequired,
            
            // Conteos
            totalTrades: allTrades.length,  // Total de la cuenta (58)
            analysisTrades: analysisTrades.length,  // Trades a analizar (20 del jueves)
            
            // Balance (de la cuenta completa)
            initialBalance,
            currentBalance,
            totalProfit,
            totalProfitPercent: (totalProfit / initialBalance) * 100,
            
            // 💰 Ganancia/Pérdida del día actual
            dayProfit,
            dayProfitPercent,
            
            // Métricas (del día actual o últimos 6 meses)
            winRate,
            wins: tradeBreakdown.wins,
            losses: tradeBreakdown.losses,
            beCount: tradeBreakdown.beCount,
            profitFactor,
            avgWin,
            avgLoss,
            currentStreak,
            allDayStreaks: this.analyzeAllStreaks(analysisTrades, initialBalance),
            bestPair,
            worstPair,
            bestHour,
            riskRewardRatio,
            newsImpactByDay,
            
            // Datos raw
            allTradesData: allTrades,
            analysisTradesData: analysisTrades
        };
    }

    // 📰 Calcular métricas por impacto en días con noticias (solo mismo día de semana)
    calculateNewsImpactByDay(trades, dayName, initialBalance) {
        const userPlan = (localStorage.getItem('userPlan') || 'free').toLowerCase();
        if (userPlan !== 'unlimited') {
            return {
                hasData: false,
                dayName: dayName || '—',
                medium: { days: 0, trades: 0, winRate: 0, dayProfit: 0, avgDayProfit: 0 },
                high: { days: 0, trades: 0, winRate: 0, dayProfit: 0, avgDayProfit: 0 }
            };
        }

        let eventsByDate = window.economicEventsByDate || null;

        if (!eventsByDate || Object.keys(eventsByDate).length === 0) {
            const cached = localStorage.getItem('economicEventsCache');
            if (cached) {
                try {
                    const events = JSON.parse(cached) || [];
                    eventsByDate = {};
                    events.forEach(event => {
                        if (!event?.date) return;
                        const dateOnly = String(event.date).split('T')[0];
                        if (!eventsByDate[dateOnly]) {
                            eventsByDate[dateOnly] = [];
                        }
                        eventsByDate[dateOnly].push(event);
                    });
                } catch (e) {
                    eventsByDate = null;
                }
            }
        }

        if (!eventsByDate || Object.keys(eventsByDate).length === 0) {
            return {
                hasData: false,
                dayName: dayName || '—',
                medium: { days: 0, trades: 0, winRate: 0, dayProfit: 0, avgDayProfit: 0 },
                high: { days: 0, trades: 0, winRate: 0, dayProfit: 0, avgDayProfit: 0 }
            };
        }

        const tradesByDate = {};
        (trades || []).forEach(t => {
            const dateKey = (t.date || t.trade_date || '').toString().split('T')[0];
            if (!dateKey) return;
            if (!tradesByDate[dateKey]) tradesByDate[dateKey] = [];
            tradesByDate[dateKey].push(t);
        });

        const stats = {
            hasData: true,
            dayName: dayName || '—',
            medium: { days: 0, trades: 0, winRate: 0, dayProfit: 0, avgDayProfit: 0 },
            high: { days: 0, trades: 0, winRate: 0, dayProfit: 0, avgDayProfit: 0 }
        };

        Object.entries(eventsByDate).forEach(([dateKey, events]) => {
            const dayTrades = tradesByDate[dateKey] || [];
            if (dayTrades.length === 0) return;

            const hasHigh = (events || []).some(ev => String(ev?.impact || '').toLowerCase() === 'high');
            const hasMedium = (events || []).some(ev => {
                const impact = String(ev?.impact || '').toLowerCase();
                return impact === 'medium' || impact === 'med';
            });

            const dayProfit = dayTrades.reduce((sum, t) => sum + (parseFloat(t.profitUSD) || 0), 0);
            const dayWinRate = this.calculateWinRate(dayTrades, initialBalance);

            if (hasHigh) {
                stats.high.days += 1;
                stats.high.trades += dayTrades.length;
                stats.high.dayProfit += dayProfit;
                stats.high.winRate += dayWinRate;
            }
            if (hasMedium) {
                stats.medium.days += 1;
                stats.medium.trades += dayTrades.length;
                stats.medium.dayProfit += dayProfit;
                stats.medium.winRate += dayWinRate;
            }
        });

        if (stats.high.days > 0) {
            stats.high.winRate = stats.high.winRate / stats.high.days;
            stats.high.avgDayProfit = stats.high.dayProfit / stats.high.days;
        }
        if (stats.medium.days > 0) {
            stats.medium.winRate = stats.medium.winRate / stats.medium.days;
            stats.medium.avgDayProfit = stats.medium.dayProfit / stats.medium.days;
        }

        return stats;
    }

    calculateBalance(trades, initialBalance) {
        let balance = initialBalance;
        trades.forEach(t => {
            balance += (parseFloat(t.profitUSD) || 0);
        });
        return balance;
    }

    calculateWinRate(trades, initialBalance) {
        if (trades.length === 0) return 0;
        const beCount = trades.filter(t => isBreakEvenTrade(t, initialBalance)).length;
        const tradesExcludingBE = trades.filter(t => !isBreakEvenTrade(t, initialBalance));
        const wins = tradesExcludingBE.filter(t => (parseFloat(t.profitUSD) || 0) > 0).length;
        return tradesExcludingBE.length > 0 ? (wins / tradesExcludingBE.length) * 100 : 0;
    }

    getTradeBreakdown(trades, initialBalance) {
        const wins = trades.filter(t => !isBreakEvenTrade(t, initialBalance) && (parseFloat(t.profitUSD) || 0) > 0).length;
        const beCount = trades.filter(t => isBreakEvenTrade(t, initialBalance)).length;
        const losses = trades.length - wins - beCount;
        return { wins, losses, beCount };
    }

    calculateProfitFactor(trades) {
        const wins = trades
            .filter(t => (parseFloat(t.profitUSD) || 0) > 0)
            .reduce((sum, t) => sum + parseFloat(t.profitUSD), 0);
        const losses = Math.abs(trades
            .filter(t => (parseFloat(t.profitUSD) || 0) < 0)
            .reduce((sum, t) => sum + parseFloat(t.profitUSD), 0));
        
        return losses > 0 ? wins / losses : 0;
    }

    calculateAvgWin(trades) {
        const wins = trades.filter(t => (parseFloat(t.profitUSD) || 0) > 0);
        if (wins.length === 0) return 0;
        const total = wins.reduce((sum, t) => sum + parseFloat(t.profitUSD), 0);
        return total / wins.length;
    }

    calculateAvgLoss(trades) {
        const losses = trades.filter(t => (parseFloat(t.profitUSD) || 0) < 0);
        if (losses.length === 0) return 0;
        const total = losses.reduce((sum, t) => sum + parseFloat(t.profitUSD), 0);
        return total / losses.length;
    }

    // Helper: Analizar todas las rachas del día (positivas y negativas)
    // 🆕 Excluye trades BE que estén entre -0.1% y 0.1% del balance
    analyzeAllStreaks(trades, initialBalance = 10000) {
        if (trades.length === 0) return { wins: [], losses: [], longestWinStreak: 0, longestLossStreak: 0 };
        
        // Filtrar trades BE
        const filteredTrades = trades.filter(t => !isBreakEvenTrade(t, initialBalance));
        if (filteredTrades.length === 0) return { wins: [], losses: [], longestWinStreak: 0, longestLossStreak: 0 };
        
        let winStreaks = [];
        let lossStreaks = [];
        let currentWinStreak = 0;
        let currentLossStreak = 0;
        
        filteredTrades.forEach(t => {
            const profit = parseFloat(t.profitUSD) || 0;
            
            if (profit > 0) {
                currentWinStreak++;
                if (currentLossStreak > 0) {
                    lossStreaks.push(currentLossStreak);
                    currentLossStreak = 0;
                }
            } else if (profit < 0) {
                currentLossStreak++;
                if (currentWinStreak > 0) {
                    winStreaks.push(currentWinStreak);
                    currentWinStreak = 0;
                }
            }
        });
        
        // Agregar la última racha si existe
        if (currentWinStreak > 0) winStreaks.push(currentWinStreak);
        if (currentLossStreak > 0) lossStreaks.push(currentLossStreak);
        
        return {
            wins: winStreaks,
            losses: lossStreaks,
            longestWinStreak: winStreaks.length > 0 ? Math.max(...winStreaks) : 0,
            longestLossStreak: lossStreaks.length > 0 ? Math.max(...lossStreaks) : 0
        };
    }

    // 🆕 Calcula la racha ACTUAL (últimos trades consecutivos del mismo tipo)
    // Excluye trades BE (-0.1% a 0.1%)
    calculateStreak(trades, initialBalance = 10000) {
        if (trades.length === 0) return { consecutive: 0, type: 'NEUTRAL' };
        
        // Filtrar trades BE
        const filteredTrades = trades.filter(t => !isBreakEvenTrade(t, initialBalance));
        if (filteredTrades.length === 0) return { consecutive: 0, type: 'NEUTRAL' };
        
        let streak = 0;
        let type = 'NEUTRAL';
        
        for (let i = filteredTrades.length - 1; i >= 0; i--) {
            const profit = parseFloat(filteredTrades[i].profitUSD) || 0;
            
            if (i === filteredTrades.length - 1) {
                type = profit > 0 ? 'WIN' : profit < 0 ? 'LOSS' : 'NEUTRAL';
                streak = 1;
            } else {
                const prevProfit = parseFloat(filteredTrades[i + 1].profitUSD) || 0;
                if ((profit > 0 && prevProfit > 0) || (profit < 0 && prevProfit < 0)) {
                    streak++;
                } else {
                    break;
                }
            }
        }
        
        return { consecutive: streak, type };
    }

    // Helper: Extract base currency from pair (EUR/USD → EUR, GBP/USD → GBP, etc)
    getBaseCurrency(pair) {
        if (!pair || pair === 'UNKNOWN') return 'UNKNOWN';
        return pair.split('/')[0] || pair.substring(0, 3);
    }

    // Helper: Calculate expectancy for a set of trades
    calculateExpectancy(trades) {
        if (trades.length === 0) return 0;
        const winningTrades = trades.filter(t => (parseFloat(t.profitUSD) || 0) > 0);
        const losingTrades = trades.filter(t => (parseFloat(t.profitUSD) || 0) < 0);
        
        const winRate = winningTrades.length / trades.length;
        const lossRate = losingTrades.length / trades.length;
        
        const avgWin = winningTrades.length > 0 
            ? winningTrades.reduce((sum, t) => sum + (parseFloat(t.profitUSD) || 0), 0) / winningTrades.length 
            : 0;
        const avgLoss = losingTrades.length > 0 
            ? Math.abs(losingTrades.reduce((sum, t) => sum + (parseFloat(t.profitUSD) || 0), 0) / losingTrades.length)
            : 0;
        
        // Expectancy = (WR × Avg Win) - (Loss Rate × Avg Loss)
        return (winRate * avgWin) - (lossRate * avgLoss);
    }

    getBestPair(trades) {
        if (trades.length === 0) return null;
        
        // 1️⃣ Agrupar por MONEDA BASE (EUR, GBP, JPY, etc) no por par exacto
        const baseStats = {};
        trades.forEach(t => {
            const baseCurrency = this.getBaseCurrency(t.pair);
            if (!baseStats[baseCurrency]) {
                baseStats[baseCurrency] = { 
                    baseCurrency, 
                    trades: [], 
                    profit: 0, 
                    count: 0,
                    pairsUsed: new Set()
                };
            }
            baseStats[baseCurrency].trades.push(t);
            baseStats[baseCurrency].profit += parseFloat(t.profitUSD) || 0;
            baseStats[baseCurrency].count++;
            baseStats[baseCurrency].pairsUsed.add(t.pair);
        });

        let bestBase = null;
        let bestExpectancy = -Infinity;
        const MIN_TRADES = 2;  // Mínimo para mostrar par
        
        // 2️⃣ Seleccionar por EXPECTANCY (métrica más importante)
        Object.values(baseStats).forEach(stat => {
            // Solo considerar con 5+ trades
            if (stat.count < MIN_TRADES) return;
            
            const expectancy = this.calculateExpectancy(stat.trades);
            const winRate = (stat.trades.filter(t => (parseFloat(t.profitUSD) || 0) > 0).length / stat.count) * 100;
            const avgProfit = stat.profit / stat.count;
            
            if (expectancy > bestExpectancy) {
                bestExpectancy = expectancy;
                bestBase = { 
                    ...stat, 
                    expectancy, 
                    winRate, 
                    avgProfit 
                };
            }
        });

        if (!bestBase) return null;

        return {
            pair: bestBase.baseCurrency,  // EUR, GBP, JPY, etc
            profit: bestBase.profit,
            count: bestBase.count,
            winRate: bestBase.winRate,
            expectancy: bestBase.expectancy,
            avgProfit: bestBase.avgProfit,
            pairsUsed: Array.from(bestBase.pairsUsed)
        };
    }

    getWorstPair(trades) {
        if (trades.length === 0) return null;
        
        // 1️⃣ Agrupar por MONEDA BASE (EUR, GBP, JPY, etc) no por par exacto
        const baseStats = {};
        trades.forEach(t => {
            const baseCurrency = this.getBaseCurrency(t.pair);
            if (!baseStats[baseCurrency]) {
                baseStats[baseCurrency] = { 
                    baseCurrency, 
                    trades: [], 
                    profit: 0, 
                    count: 0,
                    pairsUsed: new Set()
                };
            }
            baseStats[baseCurrency].trades.push(t);
            baseStats[baseCurrency].profit += parseFloat(t.profitUSD) || 0;
            baseStats[baseCurrency].count++;
            baseStats[baseCurrency].pairsUsed.add(t.pair);
        });

        let worstBase = null;
        let worstExpectancy = Infinity;
        const MIN_TRADES = 2;  // Mínimo para mostrar par
        
        // 2️⃣ Seleccionar por EXPECTANCY MÁS BAJA (peor rendimiento)
        Object.values(baseStats).forEach(stat => {
            // Solo considerar con 5+ trades
            if (stat.count < MIN_TRADES) return;
            
            const expectancy = this.calculateExpectancy(stat.trades);
            const winRate = (stat.trades.filter(t => (parseFloat(t.profitUSD) || 0) > 0).length / stat.count) * 100;
            const avgProfit = stat.profit / stat.count;
            
            if (expectancy < worstExpectancy) {
                worstExpectancy = expectancy;
                worstBase = { 
                    ...stat, 
                    expectancy, 
                    winRate, 
                    avgProfit 
                };
            }
        });

        if (!worstBase) return null;

        return {
            pair: worstBase.baseCurrency,  // EUR, GBP, JPY, etc
            profit: worstBase.profit,
            count: worstBase.count,
            winRate: worstBase.winRate,
            expectancy: worstBase.expectancy,
            avgProfit: worstBase.avgProfit,
            pairsUsed: Array.from(worstBase.pairsUsed)
        };
    }

    // Helper: Agrupar entrada por hora redonda (09:30 → 09:00, 09:45 → 09:00)
    getHourBucket(entryTime) {
        if (!entryTime || typeof entryTime !== 'string') return null;
        const timeParts = entryTime.split(':');
        if (timeParts.length < 2) return null;
        const hour = String(parseInt(timeParts[0]) || 0).padStart(2, '0');
        return `${hour}:00`;  // Agrupar en hora redonda
    }

    findBestTradingHour(trades) {
        if (trades.length === 0) return null;

        // 1️⃣ Agrupar por HORA REDONDA (09:00-10:00, no 09:30, 09:31, etc)
        const hourStats = {};
        trades.forEach(t => {
            let entryTime = t.entry_time || t.entryTime || '';
            
            // Si entry_time es timestamp, convertir
            if (!entryTime && t.timestamp) {
                const tsDate = new Date(t.timestamp);
                entryTime = `${String(tsDate.getHours()).padStart(2, '0')}:${String(tsDate.getMinutes()).padStart(2, '0')}`;
            }
            
            if (!entryTime || typeof entryTime !== 'string') return;
            
            const hourBucket = this.getHourBucket(entryTime);
            if (!hourBucket) return;
            
            if (!hourStats[hourBucket]) {
                hourStats[hourBucket] = { hourBucket, trades: [], profit: 0 };
            }
            hourStats[hourBucket].trades.push(t);
            hourStats[hourBucket].profit += parseFloat(t.profitUSD) || 0;
        });

        if (Object.keys(hourStats).length === 0) return null;

        let bestHour = null;
        let bestExpectancy = -Infinity;
        const MIN_TRADES = 5;  // Mínimo para confiar en la métrica

        // 2️⃣ Seleccionar por EXPECTANCY
        Object.values(hourStats).forEach(stat => {
            if (stat.trades.length < MIN_TRADES) return;
            
            const expectancy = this.calculateExpectancy(stat.trades);
            const winRate = (stat.trades.filter(t => (parseFloat(t.profitUSD) || 0) > 0).length / stat.trades.length) * 100;
            const avgProfit = stat.profit / stat.trades.length;
            
            if (expectancy > bestExpectancy) {
                bestExpectancy = expectancy;
                bestHour = {
                    ...stat,
                    expectancy,
                    winRate,
                    avgProfit
                };
            }
        });

        if (!bestHour) return null;

        return {
            time: bestHour.hourBucket,
            profit: bestHour.profit,
            count: bestHour.trades.length,
            winRate: bestHour.winRate,
            expectancy: bestHour.expectancy,
            avgProfit: bestHour.avgProfit,
            sampleSize: bestHour.trades.length >= 20 ? 'MUY CONFIABLE' : bestHour.trades.length >= 10 ? 'CONFIABLE' : 'MUESTRA PEQUEÑA'
        };
    }

    calculateRiskRewardRatio(trades) {
        if (trades.length === 0) return 0;
        
        const avgWin = this.calculateAvgWin(trades);
        const avgLoss = Math.abs(this.calculateAvgLoss(trades));
        
        return avgLoss > 0 ? avgWin / avgLoss : 0;
    }

    isUnlimitedPlan() {
        const plan = (sessionStorage.getItem('userPlan') || localStorage.getItem('userPlan') || 'free').toLowerCase();
        return plan === 'unlimited';
    }

    isSyncedAccount() {
        const currentAccountData = window.accounts?.[window.currentAccount];
        if (!currentAccountData) return false;
        return !!(currentAccountData.mt_account_number || currentAccountData.sync_provider || currentAccountData.external_account_id);
    }

    getPipSize(pair) {
        const symbol = String(pair || '').toUpperCase();
        if (symbol.includes('JPY')) return 0.01;
        if (symbol.includes('XAU') || symbol.includes('XAG')) return 0.1;
        return 0.0001;
    }

    calculateTradePips(trade) {
        const entry = parseFloat(trade.entryPrice ?? trade.entry_price);
        const exit = parseFloat(trade.exitPrice ?? trade.exit_price);
        if (!Number.isFinite(entry) || !Number.isFinite(exit)) return null;
        const direction = String(trade.direction || '').toUpperCase();
        const pipSize = this.getPipSize(trade.pair || trade.symbol || trade.asset || '');
        if (!pipSize) return null;
        if (direction === 'SHORT') return (entry - exit) / pipSize;
        return (exit - entry) / pipSize;
    }

    calculateTradeDurationMinutes(trade) {
        const date = (trade.date || trade.trade_date || '').toString().split('T')[0];
        const entryTime = (trade.entryTime || trade.entry_time || '').toString();
        const exitTime = (trade.exitTime || trade.exit_time || '').toString();
        if (!entryTime || !exitTime) return null;
        try {
            const entryDate = new Date(date ? `${date}T${entryTime}` : entryTime);
            const exitDate = new Date(date ? `${date}T${exitTime}` : exitTime);
            if (Number.isNaN(entryDate.getTime()) || Number.isNaN(exitDate.getTime())) return null;
            let diff = (exitDate - entryDate) / 60000;
            if (diff < 0) diff += 24 * 60;
            return diff;
        } catch (e) {
            return null;
        }
    }

    getManagementMetrics(trades) {
        const items = (trades || []).map(t => ({
            pips: this.calculateTradePips(t),
            durationMin: this.calculateTradeDurationMinutes(t),
            profit: parseFloat(t.profitUSD ?? t.profit_usd ?? t.result ?? 0)
        }));

        const pipsTrades = items.filter(i => Number.isFinite(i.pips));
        const durationTrades = items.filter(i => Number.isFinite(i.durationMin));

        const winsPips = pipsTrades.filter(i => i.profit > 0);
        const lossesPips = pipsTrades.filter(i => i.profit < 0);

        const winsDuration = durationTrades.filter(i => i.profit > 0);
        const lossesDuration = durationTrades.filter(i => i.profit < 0);

        const avg = (arr, key) => arr.length ? arr.reduce((s, i) => s + i[key], 0) / arr.length : 0;

        const avgWinPips = avg(winsPips, 'pips');
        const avgLossPips = avg(lossesPips, 'pips');
        const rrPips = Math.abs(avgLossPips) > 0 ? avgWinPips / Math.abs(avgLossPips) : 0;

        const avgHoldWinMin = avg(winsDuration, 'durationMin');
        const avgHoldLossMin = avg(lossesDuration, 'durationMin');

        return {
            pipsSample: pipsTrades.length,
            durationSample: durationTrades.length,
            avgWinPips,
            avgLossPips,
            rrPips,
            avgHoldWinMin,
            avgHoldLossMin
        };
    }

    canShowManagementMetrics(metrics) {
        if (!metrics) return false;
        if (!this.isUnlimitedPlan()) return false;
        if (!this.isSyncedAccount()) return false;
        const trades = metrics.analysisTradesData || [];
        return trades.some(t => Number.isFinite(parseFloat(t.entryPrice ?? t.entry_price)) && Number.isFinite(parseFloat(t.exitPrice ?? t.exit_price)));
    }

    getPatternRecommendation(trades) {
        const mg = this.getManagementMetrics(trades || []);
        if (mg.pipsSample < 3) {
            return {
                title: 'Recomendación de gestión',
                summary: 'Muestra insuficiente para una recomendación sólida',
                bullets: ['Necesitas al menos 3 trades con precios de entrada/salida para calcular pips reales.']
            };
        }

        const bullets = [];
        if (Math.abs(mg.avgLossPips) > Math.abs(mg.avgWinPips)) {
            bullets.push('Tus pérdidas en pips superan tus ganancias. Ajusta SL/TP o asegura parciales antes.');
        }
        if (mg.avgHoldLossMin > mg.avgHoldWinMin * 1.5) {
            bullets.push('Estás dejando correr pérdidas más tiempo que ganancias. Define un corte temporal de pérdida.');
        }
        if (mg.rrPips >= 1.2) {
            bullets.push('Tu R:R real en pips es positivo. Mantén esa estructura.');
        }
        if (bullets.length === 0) {
            bullets.push('Tu gestión es consistente. Mantén reglas y escala con disciplina.');
        }

        return {
            title: 'Recomendación de gestión',
            summary: 'Basado en tu comportamiento real de pips y tiempos',
            bullets
        };
    }

    /**
     * Genera recomendaciones basadas en el análisis
     */
    generateRecommendation(metrics) {
        if (!metrics) {
            return {
                status: 'NO_DATA',
                message: 'Sin datos para analizar',
                emoji: '📊'
            };
        }

        const { winRate, profitFactor, currentStreak, totalProfitPercent } = metrics;

        // Lógica de recomendación
        if (winRate < 40) {
            return {
                status: 'WARNING',
                message: 'Win Rate bajo - Revisar estrategia',
                emoji: '⚠️',
                color: 'from-orange-600/20 to-red-600/20'
            };
        }

        if (winRate >= 60 && profitFactor > 1.5) {
            return {
                status: 'EXCELLENT',
                message: 'Condiciones excelentes para operar',
                emoji: '🚀',
                color: 'from-green-600/20 to-emerald-600/20'
            };
        }

        if (currentStreak.consecutive > 3 && currentStreak.type === 'WIN') {
            return {
                status: 'CAUTION',
                message: 'Racha ganadora - Mantener disciplina',
                emoji: '🔥',
                color: 'from-yellow-600/20 to-orange-600/20'
            };
        }

        if (totalProfitPercent > 0) {
            return {
                status: 'GOOD',
                message: 'Operando en positivo - Seguir plan',
                emoji: '✅',
                color: 'from-blue-600/20 to-cyan-600/20'
            };
        }

        return {
            status: 'NEUTRAL',
            message: 'Condiciones moderadas',
            emoji: '⚡',
            color: 'from-slate-600/20 to-slate-500/20'
        };
    }

    /**
     * NUEVOS MÉTODOS: ANÁLISIS DE MODELOS (Phase 1)
     */

    // Analizar rendimiento de cada modelo
    analyzeModelsPerformance(trades) {
        if (!trades || trades.length === 0) return {};

        const modelStats = {};
        const accountData = window.accounts?.[window.currentAccount];
        const initialBalance = accountData?.initial_balance || accountData?.balance || 10000;

        trades.forEach(trade => {
            let modelName = null;
            const snapshot = trade.entryModelSnapshot || trade.entry_model_snapshot;
            
            if (snapshot) {
                if (typeof snapshot === 'string') {
                    try {
                        const parsed = JSON.parse(snapshot);
                        modelName = parsed.name || parsed.id || 'Unknown Model';
                    } catch (e) {
                        modelName = 'Parse Error';
                    }
                } else if (typeof snapshot === 'object') {
                    modelName = snapshot.name || snapshot.id || 'Unknown Model';
                }
            }
            
            // Si no hay modelo, registrar como "Sin Modelo"
            if (!modelName) modelName = 'Sin Modelo';

            if (!modelStats[modelName]) {
                modelStats[modelName] = {
                    name: modelName,
                    trades: [],
                    winCount: 0,
                    lossCount: 0,
                    beCount: 0,
                    totalProfit: 0,
                    totalBets: 0
                };
            }

            const profit = parseFloat(trade.profitUSD) || 0;
            const profitPct = (profit / initialBalance) * 100;
            const isBE = profitPct >= -0.1 && profitPct <= 0.1;

            modelStats[modelName].trades.push(trade);
            modelStats[modelName].totalProfit += profit;
            modelStats[modelName].totalBets += 1;

            if (isBE) modelStats[modelName].beCount += 1;
            else if (profit > 0) modelStats[modelName].winCount += 1;
            else if (profit < 0) modelStats[modelName].lossCount += 1;
        });

        // Calcular métricas por modelo (WR excluye BE, igual que vista mensual)
        Object.keys(modelStats).forEach(modelName => {
            const stats = modelStats[modelName];
            const nonBE = stats.totalBets - stats.beCount;
            stats.winRate = nonBE > 0 ? (stats.winCount / nonBE * 100) : 0;
            const grossWin = stats.trades.filter(t => parseFloat(t.profitUSD) > 0).reduce((s, t) => s + parseFloat(t.profitUSD), 0);
            const grossLoss = Math.abs(stats.trades.filter(t => parseFloat(t.profitUSD) < 0).reduce((s, t) => s + parseFloat(t.profitUSD), 0));
            stats.profitFactor = grossLoss === 0 ? (grossWin > 0 ? 99.99 : 0) : grossWin / grossLoss;
        });

        return modelStats;
    }

    // Análisis de modelo por día de la semana
    analyzeModelsByDayOfWeek(trades) {
        if (!trades || trades.length === 0) return {};

        const dayAnalysis = {};

        trades.forEach(trade => {
            let modelName = null;
            const snapshot = trade.entryModelSnapshot || trade.entry_model_snapshot;
            
            if (snapshot) {
                if (typeof snapshot === 'string') {
                    try {
                        const parsed = JSON.parse(snapshot);
                        modelName = parsed.name || parsed.id || 'Unknown Model';
                    } catch (e) {
                        modelName = 'Parse Error';
                    }
                } else if (typeof snapshot === 'object') {
                    modelName = snapshot.name || snapshot.id || 'Unknown Model';
                }
            }
            if (!modelName) modelName = 'Sin Modelo';

            const tradeDate = new Date(trade.date || new Date());
            const dayName = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'][tradeDate.getDay()];

            const key = `${modelName}|${dayName}`;
            if (!dayAnalysis[key]) {
                dayAnalysis[key] = {
                    model: modelName,
                    day: dayName,
                    trades: 0,
                    wins: 0,
                    losses: 0,
                    profit: 0
                };
            }

            dayAnalysis[key].trades += 1;
            dayAnalysis[key].profit += parseFloat(trade.profitUSD) || 0;
            if ((trade.profitUSD || 0) > 0) dayAnalysis[key].wins += 1;
            else if ((trade.profitUSD || 0) < 0) dayAnalysis[key].losses += 1;
        });

        // Calcular WinRate
        Object.keys(dayAnalysis).forEach(key => {
            const entry = dayAnalysis[key];
            entry.winRate = entry.trades > 0 ? (entry.wins / entry.trades * 100) : 0;
        });

        return dayAnalysis;
    }

    // Ranking de modelos por profitabilidad
    rankModelsByProfitability(trades) {
        const modelStats = this.analyzeModelsPerformance(trades);
        const ranking = Object.values(modelStats)
            .sort((a, b) => b.totalProfit - a.totalProfit)
            .map((model, idx) => ({
                ...model,
                rank: idx + 1
            }));
        return ranking;
    }

    // Análisis de modelo por hora (Phase 2)
    analyzeModelsByHour(trades) {
        if (!trades || trades.length === 0) return {};

        const hourAnalysis = {};

        trades.forEach(trade => {
            let modelName = null;
            const snapshot = trade.entryModelSnapshot || trade.entry_model_snapshot;
            
            if (snapshot) {
                if (typeof snapshot === 'string') {
                    try {
                        const parsed = JSON.parse(snapshot);
                        modelName = parsed.name || parsed.id || 'Unknown Model';
                    } catch (e) {
                        modelName = 'Parse Error';
                    }
                } else if (typeof snapshot === 'object') {
                    modelName = snapshot.name || snapshot.id || 'Unknown Model';
                }
            }
            if (!modelName) modelName = 'Sin Modelo';

            // Extraer hora de entrada del trade
            // Campos posibles: entryTime, entry_time (formato HH:MM), o timestamp (ISO 8601)
            let tradeHour = null;
            
            // PRIORIDAD 1: Intentar entryTime (HH:MM format)
            const entryTime = trade.entryTime || trade.entry_time;
            if (entryTime && typeof entryTime === 'string' && entryTime.includes(':')) {
                try {
                    const timeParts = entryTime.split(':');
                    const hour = parseInt(timeParts[0], 10);
                    if (!isNaN(hour) && hour >= 0 && hour <= 23) {
                        tradeHour = String(hour).padStart(2, '0');
                    }
                } catch (e) {
                    // Ignorar error
                }
            }
            
            // PRIORIDAD 2: Intentar timestamp (ISO 8601 format: 2024-02-23T09:15:00Z)
            if (!tradeHour && (trade.timestamp || trade.time_open)) {
                try {
                    const timestamp = trade.timestamp || trade.time_open;
                    if (typeof timestamp === 'string' && timestamp.length > 0) {
                        const tradeDate = new Date(timestamp);
                        if (!isNaN(tradeDate.getTime())) {
                            tradeHour = String(tradeDate.getHours()).padStart(2, '0');
                        }
                    }
                } catch (e) {
                    // Ignorar error
                }
            }

            // Si aún no hay hora válida, saltar este trade
            if (!tradeHour) return;
            
            const key = `${modelName}|${tradeHour}`;

            if (!hourAnalysis[key]) {
                hourAnalysis[key] = {
                    model: modelName,
                    hour: tradeHour,
                    trades: 0,
                    wins: 0,
                    losses: 0,
                    profit: 0
                };
            }

            hourAnalysis[key].trades += 1;
            hourAnalysis[key].profit += parseFloat(trade.profitUSD) || 0;
            if ((trade.profitUSD || 0) > 0) hourAnalysis[key].wins += 1;
            else if ((trade.profitUSD || 0) < 0) hourAnalysis[key].losses += 1;
        });

        // Calcular WinRate
        Object.keys(hourAnalysis).forEach(key => {
            const entry = hourAnalysis[key];
            entry.winRate = entry.trades > 0 ? (entry.wins / entry.trades * 100) : 0;
        });

        return hourAnalysis;
    }

    // 🔥 Phase 3: DETECTAR HOT SETUPS (Combinaciones ganadoras)
    detectHotSetups(trades) {
        if (!trades || trades.length === 0) return [];

        const setupMap = {};

        trades.forEach(trade => {
            // Extraer modelo
            let modelName = null;
            const snapshot = trade.entryModelSnapshot || trade.entry_model_snapshot;
            
            if (snapshot) {
                if (typeof snapshot === 'string') {
                    try {
                        const parsed = JSON.parse(snapshot);
                        modelName = parsed.name || parsed.id || 'Unknown Model';
                    } catch (e) {
                        modelName = 'Parse Error';
                    }
                } else if (typeof snapshot === 'object') {
                    modelName = snapshot.name || snapshot.id || 'Unknown Model';
                }
            }
            if (!modelName) modelName = 'Sin Modelo';

            // Extraer día de la semana
            const tradeDate = new Date(trade.date || new Date());
            const dayName = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'][tradeDate.getDay()];

            // Extraer hora
            let tradeHour = null;
            const entryTime = trade.entryTime || trade.entry_time;
            if (entryTime && typeof entryTime === 'string' && entryTime.includes(':')) {
                try {
                    const timeParts = entryTime.split(':');
                    const hour = parseInt(timeParts[0], 10);
                    if (!isNaN(hour) && hour >= 0 && hour <= 23) {
                        tradeHour = String(hour).padStart(2, '0');
                    }
                } catch (e) {
                    // Ignorar
                }
            }
            if (!tradeHour && (trade.timestamp || trade.time_open)) {
                try {
                    const timestamp = trade.timestamp || trade.time_open;
                    if (typeof timestamp === 'string' && timestamp.length > 0) {
                        const tradeDate = new Date(timestamp);
                        if (!isNaN(tradeDate.getTime())) {
                            tradeHour = String(tradeDate.getHours()).padStart(2, '0');
                        }
                    }
                } catch (e) {
                    // Ignorar
                }
            }
            if (!tradeHour) return;

            // Crear key: model|day|hour
            const key = `${modelName}|${dayName}|${tradeHour}`;

            if (!setupMap[key]) {
                setupMap[key] = {
                    model: modelName,
                    day: dayName,
                    hour: tradeHour,
                    trades: 0,
                    wins: 0,
                    losses: 0,
                    profit: 0
                };
            }

            setupMap[key].trades += 1;
            setupMap[key].profit += parseFloat(trade.profitUSD) || 0;
            if ((trade.profitUSD || 0) > 0) setupMap[key].wins += 1;
            else if ((trade.profitUSD || 0) < 0) setupMap[key].losses += 1;
        });

        // Calcular WinRate y Confidence Score
        const setupsList = Object.values(setupMap).map(setup => ({
            ...setup,
            winRate: setup.trades > 0 ? (setup.wins / setup.trades * 100) : 0,
            expectancy: setup.trades > 0 ? (setup.profit / setup.trades) : 0,
            confidence: this.calculateConfidenceScore(setup.trades)
        }));

        // Ordenar por: 1) WinRate desc 2) Profit desc 3) Trades desc
        return setupsList.sort((a, b) => {
            if (b.winRate !== a.winRate) return b.winRate - a.winRate;
            if (b.profit !== a.profit) return b.profit - a.profit;
            return b.trades - a.trades;
        });
    }

    // Calcular confidence score basado en número de trades
    calculateConfidenceScore(tradeCount) {
        if (tradeCount < 2) return 0;
        if (tradeCount < 5) return 30;
        if (tradeCount < 10) return 60;
        if (tradeCount < 20) return 85;
        return 100;
    }

    // Generar HTML para Hot Setups
    generateHotSetupsHTML(trades) {
        const hotSetups = this.detectHotSetups(trades);

        if (hotSetups.length === 0) {
            return '<p class="text-xs text-slate-400">No hay suficientes datos para detectar hot setups.</p>';
        }

        // Filtrar top 5 hot setups con WR >= 50%
        const topSetups = hotSetups.filter(s => s.winRate >= 50 && s.confidence >= 60).slice(0, 5);

        if (topSetups.length === 0) {
            return '<p class="text-xs text-slate-400">No se encontraron hot setups con WR ≥50% y confianza ≥60.</p>';
        }

        let html = `
            <div class="bg-gradient-to-b from-slate-900/60 to-slate-950/60 rounded-lg p-4 border border-slate-700/50 mt-4">
                <div class="flex items-center gap-2 mb-4">
                    <div class="w-8 h-8 rounded-lg bg-red-600/30 border border-red-500/30 flex items-center justify-center text-lg">🔥</div>
                    <p class="text-xs font-bold text-slate-300 uppercase">Hot Setups - Combos Ganadores</p>
                    <span class="text-[10px] text-slate-500 ml-auto">${topSetups.length} detectados</span>
                </div>

                <div class="space-y-3">
        `;

        topSetups.forEach((setup, idx) => {
            const rank = idx + 1;
            const confidenceBar = setup.confidence;
            const medal = rank === 1 ? '🥇' : rank === 2 ? '🥈' : rank === 3 ? '🥉' : '⭐';
            const profitColor = setup.profit > 0 ? 'text-emerald-400' : 'text-red-400';
            const profitStr = setup.profit > 0 ? `+$${setup.profit.toFixed(0)}` : `-$${Math.abs(setup.profit).toFixed(0)}`;

            html += `
                <div class="bg-slate-800/40 rounded-lg border border-slate-700/60 overflow-hidden hover:border-red-500/40 transition">
                    <!-- Header del Setup -->
                    <div class="bg-gradient-to-r from-red-950/40 to-slate-900/40 p-3 border-b border-slate-700/50">
                        <div class="flex items-center justify-between gap-2">
                            <div class="flex items-center gap-3">
                                <span class="text-xl">${medal}</span>
                                <div>
                                    <p class="text-xs font-bold text-white">${setup.model}</p>
                                    <p class="text-xs text-slate-400 mt-0.5">${setup.day} · ${setup.hour}:00 - ${setup.hour}:59</p>
                                </div>
                            </div>
                            <div class="text-right">
                                <p class="text-sm font-bold ${profitColor}">${profitStr}</p>
                                <p class="text-xs text-emerald-400 font-bold">${setup.winRate.toFixed(0)}% WR</p>
                            </div>
                        </div>
                    </div>

                    <!-- Estadísticas -->
                    <div class="p-3 space-y-2">
                        <div class="grid grid-cols-4 gap-2 mb-2">
                            <div class="bg-slate-900/60 rounded-lg p-2 border border-slate-700/40 text-center">
                                <p class="text-[10px] text-slate-400 uppercase">Trades</p>
                                <p class="text-sm font-bold text-white mt-1">${setup.trades}</p>
                            </div>
                            <div class="bg-emerald-900/30 rounded-lg p-2 border border-emerald-500/30 text-center">
                                <p class="text-[10px] text-emerald-400 uppercase">Wins</p>
                                <p class="text-sm font-bold text-emerald-300 mt-1">${setup.wins}</p>
                            </div>
                            <div class="bg-red-900/30 rounded-lg p-2 border border-red-500/30 text-center">
                                <p class="text-[10px] text-red-400 uppercase">Losses</p>
                                <p class="text-sm font-bold text-red-300 mt-1">${setup.losses}</p>
                            </div>
                            <div class="bg-blue-900/30 rounded-lg p-2 border border-blue-500/30 text-center">
                                <p class="text-[10px] text-blue-400 uppercase">Expectancy</p>
                                <p class="text-sm font-bold text-blue-300 mt-1">$${setup.expectancy.toFixed(0)}</p>
                            </div>
                        </div>

                        <!-- Confidence Score -->
                        <div>
                            <div class="flex justify-between items-center mb-1">
                                <span class="text-xs text-slate-400">Confianza</span>
                                <span class="text-xs font-bold text-slate-200">${setup.confidence}%</span>
                            </div>
                            <div class="w-full bg-slate-900/60 rounded-full h-2 border border-slate-700/40 overflow-hidden">
                                <div class="h-full bg-gradient-to-r from-red-500 to-red-600 rounded-full" style="width: ${setup.confidence}%"></div>
                            </div>
                            <p class="text-[10px] text-slate-500 mt-1">
                                💡 ${setup.confidence >= 85 ? '✅ MUY CONFIABLE' : setup.confidence >= 60 ? '⚠️ CONFIABLE' : '❓ BAJO DATOS'}
                            </p>
                        </div>

                        <!-- Recomendación -->
                        <div class="bg-slate-900/80 rounded-lg p-2 border border-slate-700/40 mt-2">
                            <p class="text-[11px] text-slate-300">
                                ${setup.confidence >= 85 
                                    ? `✅ <strong>FOCUS SETUP</strong> - Usar en próximas sesiones. ${setup.winRate.toFixed(0)}% WR garantiza rentabilidad.` 
                                    : `⚠️ <strong>MONITOR</strong> - Necesita más validación (${setup.trades} trades detectados).`
                                }
                            </p>
                        </div>
                    </div>
                </div>
            `;
        });

        // Summary card
        html += `
                </div>

                <!-- Summary -->
                <div class="bg-slate-800/40 rounded-lg p-3 border border-slate-700/50 mt-4">
                    <p class="text-xs font-bold text-slate-300 mb-2">📊 Resumen de Hot Setups</p>
                    <div class="text-xs text-slate-400 space-y-1">
                        <p>• Total detectados: <span class="text-slate-200 font-semibold">${hotSetups.length}</span></p>
                        <p>• Con WR ≥50% y Confianza ≥60: <span class="text-slate-200 font-semibold">${topSetups.length}</span></p>
                        <p>• Top setup generaría: <span class="text-emerald-400 font-semibold">${topSetups[0].profitStr || '+$' + topSetups[0].profit.toFixed(0)}</span></p>
                        <p class="mt-2 pt-2 border-t border-slate-700/40">
                            🎯 <strong>Estrategia:</strong> Enfócate en hotspots con confianza ≥85%. Ignora los de <60%.
                        </p>
                    </div>
                </div>
            </div>
        `;

        return html;
    }

    // Generar Heatmap HTML (Phase 2)
    generateHeatmapHTML(trades) {
        const ranking = this.rankModelsByProfitability(trades);
        const hourAnalysis = this.analyzeModelsByHour(trades);

        if (ranking.length === 0 || Object.keys(hourAnalysis).length === 0) {
            return '<p class="text-xs text-slate-400">Sin datos de horarios para generar heatmap.</p>';
        }

        // Agrupar por modelo
        const modelHours = {};
        Object.values(hourAnalysis).forEach(entry => {
            if (!modelHours[entry.model]) modelHours[entry.model] = {};
            modelHours[entry.model][entry.hour] = entry;
        });

        // Listado de horas (0-23)
        const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));

        // Encontrar valor máximo para escala
        const maxWR = Math.max(...Object.values(hourAnalysis).map(e => e.winRate), 50);

        let html = `
            <div class="bg-gradient-to-b from-slate-900/60 to-slate-950/60 rounded-lg p-4 border border-slate-700/50 mt-4 overflow-x-auto">
                <div class="flex items-center gap-2 mb-4">
                    <div class="w-8 h-8 rounded-lg bg-pink-600/30 border border-pink-500/30 flex items-center justify-center text-lg">🔥</div>
                    <p class="text-xs font-bold text-slate-300 uppercase">Heatmap: WinRate por Hora</p>
                </div>

                <div class="space-y-4">
        `;

        // Generar heatmap para cada modelo
        Object.keys(modelHours).sort().forEach(modelName => {
            const hoursData = modelHours[modelName];
            
            html += `
                <div class="bg-slate-800/30 p-4 rounded-lg border border-slate-700/40">
                    <p class="text-xs font-bold text-white mb-3">${modelName}</p>
                    
                    <!-- Grid de horas -->
                    <div class="overflow-x-auto scrollbar-thin scrollbar-thumb-amber-500/40 scrollbar-track-slate-800/30 hover:scrollbar-thumb-amber-400/60">
                        <div class="grid gap-1 pb-2" style="grid-template-columns: repeat(${Math.min(24, 12)}, 1fr); min-width: 100%;">
            `;

            // Mostrar cada hora
            hours.forEach(hour => {
                const hourData = hoursData[hour];
                if (!hourData) {
                    html += `
                        <div class="text-center rounded-lg bg-slate-900/50 border border-slate-700/40 p-2 text-[10px] text-slate-600">
                            <div class="font-semibold">${hour}:00</div>
                            <div>—</div>
                        </div>
                    `;
                } else {
                    const intensity = Math.min(100, (hourData.winRate / maxWR) * 100);
                    let rgbValues, textColor, borderColor;

                    if (hourData.winRate >= 70) {
                        rgbValues = [5, 150, 105];
                        textColor = 'text-emerald-200';
                        borderColor = 'border-emerald-500/60';
                    } else if (hourData.winRate >= 60) {
                        rgbValues = [34, 197, 94];
                        textColor = 'text-emerald-100';
                        borderColor = 'border-emerald-400/60';
                    } else if (hourData.winRate >= 50) {
                        rgbValues = [180, 150, 50];
                        textColor = 'text-amber-100';
                        borderColor = 'border-amber-400/60';
                    } else {
                        rgbValues = [139, 20, 20];
                        textColor = 'text-red-100';
                        borderColor = 'border-red-400/60';
                    }

                    const alpha = intensity / 100 * 0.8 + 0.2;
                    const bgColorRgba = `rgba(${rgbValues[0]}, ${rgbValues[1]}, ${rgbValues[2]}, ${alpha})`;

                    html += `
                        <div class="text-center rounded-lg border ${borderColor} p-2 text-[10px] font-bold ${textColor} transition-all hover:scale-110 hover:shadow-lg cursor-pointer relative group"
                             style="background-color: ${bgColorRgba};">
                            <div class="font-semibold">${hour}h</div>
                            <div>${hourData.winRate.toFixed(0)}%</div>
                            <div class="text-[8px] text-slate-300 opacity-0 group-hover:opacity-100">${hourData.trades} trades</div>
                            
                            <!-- Tooltip -->
                            <div class="hidden group-hover:block absolute -top-10 left-1/2 -translate-x-1/2 bg-slate-900 border border-slate-600 rounded px-2 py-1 text-xs whitespace-nowrap z-50">
                                ${hourData.wins}W / ${hourData.losses}L (${hourData.profit > 0 ? '+' : ''}$${ hourData.profit.toFixed(0)})
                            </div>
                        </div>
                    `;
                }
            });

            html += `
                        </div>
                    </div>
                </div>
            `;
        });

        html += `
                </div>

                <!-- Legend -->
                <div class="mt-4 p-3 bg-slate-800/30 rounded-lg border border-slate-700/40 text-xs text-slate-400">
                    <p class="mb-2 font-bold text-slate-300">🎨 Escala de WinRate:</p>
                    <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
                        <div class="flex items-center gap-2">
                            <div class="w-4 h-4 rounded bg-emerald-600"></div>
                            <span>≥70%</span>
                        </div>
                        <div class="flex items-center gap-2">
                            <div class="w-4 h-4 rounded bg-emerald-500"></div>
                            <span>60-70%</span>
                        </div>
                        <div class="flex items-center gap-2">
                            <div class="w-4 h-4 rounded bg-amber-500"></div>
                            <span>50-60%</span>
                        </div>
                        <div class="flex items-center gap-2">
                            <div class="w-4 h-4 rounded bg-red-700"></div>
                            <span>&lt;50%</span>
                        </div>
                    </div>
                    <p class="mt-2 text-slate-500">💡 Hover sobre las celdas para ver: Wins/Losses y Profit</p>
                </div>
            </div>
        `;

        return html;
    }

    // Generar HTML tabla de modelos (Phase 1 - MEJORADO)
    generateModelsAnalysisHTML(trades) {
        const ranking = this.rankModelsByProfitability(trades);
        const dayAnalysis = this.analyzeModelsByDayOfWeek(trades);

        if (ranking.length === 0) {
            return '<p class="text-xs text-slate-400">Sin datos de modelos para analizar.</p>';
        }

        // Tabla Comparativa Mejorada
        let html = `
            <div class="bg-gradient-to-b from-slate-900/60 to-slate-950/60 rounded-lg p-4 border border-slate-700/50 overflow-x-auto">
                <div class="flex items-center gap-2 mb-3">
                    <div class="w-8 h-8 rounded-lg bg-blue-600/30 border border-blue-500/30 flex items-center justify-center text-lg">📊</div>
                    <p class="text-xs font-bold text-slate-300 uppercase">Rendimiento por Modelo</p>
                </div>
                
                <table class="w-full text-xs border-collapse">
                    <thead>
                        <tr class="border-b border-slate-600/60">
                            <th class="text-left py-3 px-3 font-bold text-slate-300">Rk</th>
                            <th class="text-left py-3 px-3 font-bold text-slate-300">Modelo</th>
                            <th class="text-center py-3 px-3 font-bold text-slate-300">Trades</th>
                            <th class="text-center py-3 px-3 font-bold text-slate-300">WinRate</th>
                            <th class="text-center py-3 px-3 font-bold text-slate-300">W/L</th>
                            <th class="text-right py-3 px-3 font-bold text-slate-300">Profit</th>
                        </tr>
                    </thead>
                    <tbody>
        `;

        ranking.forEach(model => {
            const profitColor = model.totalProfit > 0 ? 'text-emerald-400' : model.totalProfit < 0 ? 'text-red-400' : 'text-slate-400';
            const wrBg = model.winRate >= 65 ? 'bg-emerald-900/30 border-emerald-500/40' : model.winRate >= 55 ? 'bg-amber-900/30 border-amber-500/40' : 'bg-red-900/30 border-red-500/40';
            const wrColor = model.winRate >= 65 ? 'text-emerald-300' : model.winRate >= 55 ? 'text-amber-300' : 'text-red-300';
            const profitStr = model.totalProfit >= 0 ? `+$${model.totalProfit.toFixed(0)}` : `-$${Math.abs(model.totalProfit).toFixed(0)}`;
            const medal = model.rank === 1 ? '🥇' : model.rank === 2 ? '🥈' : model.rank === 3 ? '🥉' : '•';

            html += `
                <tr class="border-b border-slate-700/30 hover:bg-slate-800/40 transition-colors">
                    <td class="py-2.5 px-3 text-slate-400">${medal}</td>
                    <td class="py-2.5 px-3">
                        <span class="font-semibold text-white">${model.name}</span>
                    </td>
                    <td class="py-2.5 px-3 text-center text-slate-300">${model.totalBets}</td>
                    <td class="py-2.5 px-3 text-center">
                        <span class="px-2 py-1 rounded-full text-xs font-bold ${wrBg} ${wrColor} border">${model.winRate.toFixed(0)}%</span>
                    </td>
                    <td class="py-2.5 px-3 text-center text-slate-300">
                        <span class="text-emerald-400">${model.winCount}</span> / <span class="text-red-400">${model.lossCount}</span>
                    </td>
                    <td class="py-2.5 px-3 text-right font-bold ${profitColor} text-sm">${profitStr}</td>
                </tr>
            `;
        });

        html += `
                    </tbody>
                </table>
                <div class="mt-3 pt-3 border-t border-slate-700/40 text-xs text-slate-500">
                    💡 Tip: Los modelos con WR ≥65% son candidatos para escalar
                </div>
            </div>

            <!-- Análisis por Día de Semana MEJORADO -->
            <div class="bg-gradient-to-b from-slate-900/60 to-slate-950/60 rounded-lg p-4 border border-slate-700/50 mt-4">
                <div class="flex items-center gap-2 mb-3">
                    <div class="w-8 h-8 rounded-lg bg-purple-600/30 border border-purple-500/30 flex items-center justify-center text-lg">📅</div>
                    <p class="text-xs font-bold text-slate-300 uppercase">WinRate por Día</p>
                </div>
                <div class="space-y-3">
        `;

        // Agrupar por modelo
        const modelDays = {};
        Object.values(dayAnalysis).forEach(entry => {
            if (!modelDays[entry.model]) modelDays[entry.model] = [];
            modelDays[entry.model].push(entry);
        });

        // Mostrar análisis por modelo
        Object.keys(modelDays).sort().forEach(modelName => {
            const dayStats = modelDays[modelName];
            const modelInfo = ranking.find(m => m.name === modelName);
            const modelColor = modelInfo && modelInfo.winRate >= 60 ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-slate-500';

            html += `
                <div class="bg-slate-800/30 p-3 rounded-lg border border-slate-700/40 ${modelColor}">
                    <div class="flex items-center justify-between mb-2">
                        <p class="text-xs font-bold text-white">${modelName}</p>
                        <span class="text-xs text-slate-400">${dayStats.length} días</span>
                    </div>
                    <div class="grid grid-cols-4 sm:grid-cols-7 gap-1.5 text-xs">
            `;

            const daysOrder = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo'];
            daysOrder.forEach(day => {
                const entry = dayStats.find(d => d.day === day);
                if (!entry) {
                    html += `
                        <div class="text-center py-2 px-1 rounded bg-slate-900/50 text-slate-600 text-[10px]">
                            <div class="font-semibold">${day.substring(0, 2)}</div>
                            <div>—</div>
                        </div>
                    `;
                } else {
                    const wrBg = entry.winRate >= 65 ? 'bg-emerald-900/40 border-emerald-500/50' : entry.winRate >= 55 ? 'bg-amber-900/40 border-amber-500/50' : 'bg-red-900/40 border-red-500/50';
                    const wrText = entry.winRate >= 65 ? 'text-emerald-300' : entry.winRate >= 55 ? 'text-amber-300' : 'text-red-300';
                    html += `
                        <div class="text-center py-2 px-1 rounded ${wrBg} border ${wrText} font-bold text-[10px] transition-colors hover:scale-105">
                            <div>${day.substring(0, 2)}</div>
                            <div>${entry.winRate.toFixed(0)}%</div>
                        </div>
                    `;
                }
            });

            html += `
                    </div>
                </div>
            `;
        });

        html += `
                </div>
                <div class="mt-3 p-3 bg-slate-800/30 rounded-lg border border-slate-700/40 text-xs text-slate-400">
                    <p class="mb-1">🎯 <strong>Color Codes:</strong></p>
                    <div class="flex gap-2 flex-wrap text-[11px]">
                        <span><span class="text-emerald-300">●</span> WR ≥65%</span>
                        <span><span class="text-amber-300">●</span> WR 55-65%</span>
                        <span><span class="text-red-300">●</span> WR <55%</span>
                    </div>
                </div>
            </div>

            <!-- HEATMAP - Phase 2 -->
            ${this.generateHeatmapHTML(trades)}
        `;

        return html;
    }

    render() {
        const analysisMode = localStorage.getItem('iaScannerMode') || 'day';
        const minTradesRequired = parseInt(localStorage.getItem('iaScannerMinTrades') || '2', 10);
        const metrics = this.getMetrics();

        if (!metrics) {
            const scopeLabel = analysisMode === 'day' ? 'del día actual' : 'de los últimos 6 meses';
            this.modal.innerHTML = `
                <div class="p-6 sm:p-8 text-center space-y-4">
                    <div class="text-5xl">📊</div>
                    <div>
                        <h3 class="text-xl font-bold text-white mb-2">Sin Datos para Analizar</h3>
                        <p class="text-gray-400">Necesitas al menos ${minTradesRequired} trades ${scopeLabel} para ver el análisis</p>
                    </div>

                    <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50 flex flex-col sm:flex-row sm:items-center gap-3">
                        <div class="flex gap-2 justify-center sm:justify-start">
                            <button data-ia-mode="day" class="px-3 py-1.5 rounded-lg text-xs font-semibold ${analysisMode === 'day' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-300 border border-slate-700'}">Día actual</button>
                            <button data-ia-mode="6m" class="px-3 py-1.5 rounded-lg text-xs font-semibold ${analysisMode === '6m' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-300 border border-slate-700'}">Últimos 6 meses</button>
                        </div>
                        <div class="flex items-center gap-2 text-xs text-slate-400 justify-center sm:justify-start">
                            <span>Mín. trades</span>
                            <select id="iaMinTradesSelect" class="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-white">
                                <option value="2" ${minTradesRequired === 2 ? 'selected' : ''}>2</option>
                                <option value="5" ${minTradesRequired === 5 ? 'selected' : ''}>5</option>
                                <option value="10" ${minTradesRequired === 10 ? 'selected' : ''}>10</option>
                            </select>
                        </div>
                    </div>

                    <div class="flex flex-col sm:flex-row gap-2 justify-center">
                        <button onclick="window.iaScannerSAAS.render()" class="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-6 rounded-lg transition">
                            Reintentar
                        </button>
                        <button onclick="window.iaScannerSAAS.close()" class="bg-slate-700 hover:bg-slate-600 text-white font-bold py-2 px-6 rounded-lg transition">
                            Cerrar
                        </button>
                    </div>
                </div>
            `;
            this.bindUI();
            return;
        }

        const analysis = this.generateAIAnalysis(metrics);
        const score = this.calculateScore(metrics);
        const scoreLabel = this.getScoreLabel(score);
        const recommendations = this.getTopRecommendations(metrics);
        const warnings = this.getWarnings(metrics);
        const impulse = this.analyzeImpulseBehavior(metrics.analysisTradesData);
        const canShowManagement = this.canShowManagementMetrics(metrics);
        const management = canShowManagement ? this.getManagementMetrics(metrics.analysisTradesData) : null;
        const patternRecommendation = this.getPatternRecommendation(metrics.analysisTradesData);
        const mgAvgWin = management && Number.isFinite(management.avgWinPips) ? management.avgWinPips.toFixed(1) : '—';
        const mgAvgLoss = management && Number.isFinite(management.avgLossPips) ? Math.abs(management.avgLossPips).toFixed(1) : '—';
        const mgRR = management && Number.isFinite(management.rrPips) ? management.rrPips.toFixed(2) : '—';
        const mgHoldWin = management && Number.isFinite(management.avgHoldWinMin) ? management.avgHoldWinMin.toFixed(0) : '—';
        const mgHoldLoss = management && Number.isFinite(management.avgHoldLossMin) ? management.avgHoldLossMin.toFixed(0) : '—';
        const currentMode = metrics.analysisMode || analysisMode;
        const currentMinTrades = metrics.minTradesRequired || minTradesRequired;

        // HTML Completo con análisis exhaustivo
        this.modal.innerHTML = `
            <style>
                @keyframes slideInDown {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                @keyframes pulse-glow {
                    0%, 100% { box-shadow: 0 0 20px rgba(59, 130, 246, 0.3); }
                    50% { box-shadow: 0 0 30px rgba(59, 130, 246, 0.5); }
                }
                .ia-modal-header {
                    animation: slideInDown 0.6s ease-out;
                }
                .ia-main-card {
                    animation: slideInDown 0.7s ease-out 0.1s both;
                }
                .modal-scroll {
                    scrollbar-width: thin;
                    scrollbar-color: rgba(59, 130, 246, 0.5) transparent;
                }
                .modal-scroll::-webkit-scrollbar {
                    width: 8px;
                }
                .modal-scroll::-webkit-scrollbar-track {
                    background: rgba(15, 23, 42, 0.5);
                    border-radius: 999px;
                }
                .modal-scroll::-webkit-scrollbar-thumb {
                    background: linear-gradient(180deg, rgba(59, 130, 246, 0.8), rgba(34, 211, 238, 0.8));
                    border-radius: 999px;
                    border: 2px solid rgba(15, 23, 42, 0.6);
                }
                .modal-scroll::-webkit-scrollbar-thumb:hover {
                    background: linear-gradient(180deg, rgba(59, 130, 246, 1), rgba(34, 211, 238, 1));
                }
            </style>

            <!-- HEADER MEJORADO - RESPONSIVE -->
            <div class="ia-modal-header bg-gradient-to-r from-slate-950 via-blue-950/40 to-slate-950 border-b border-blue-500/30 px-3 sm:px-6 py-4 sm:py-5 flex justify-between items-center gap-2">
                <div class="flex-1 min-w-0">
                    <h2 class="text-sm sm:text-xl lg:text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 flex items-center gap-1 sm:gap-3 flex-wrap">
                        ${this.getIconSvg('bolt', 'w-5 h-5 sm:w-6 sm:h-6 text-blue-400')}
                        <span class="truncate">IA SCANNER - ${(metrics.dayName || 'UNKNOWN').toUpperCase()}</span>
                        <span class="hidden sm:inline">${this.getIconSvg('target', 'w-4 h-4 text-cyan-300')}</span>
                    </h2>
                    <p class="text-xs text-blue-300/70 mt-1 hidden sm:block">Análisis inteligente de hoy</p>
                </div>
                <div class="flex gap-2 flex-shrink-0">
                    <button onclick="window.iaScannerSAAS.close()" class="text-xl sm:text-2xl text-gray-400 hover:text-white transition hover:rotate-90 duration-300">✕</button>
                </div>
            </div>

            <!-- CONTENT SCROLLEABLE -->
            <div class="p-3 sm:p-4 space-y-3 sm:space-y-4 overflow-y-auto flex-1 min-h-0 modal-scroll">

                <!-- CONTROLES -->
                <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50 flex flex-col sm:flex-row sm:items-center gap-3">
                    <div class="flex gap-2">
                        <button data-ia-mode="day" class="px-3 py-1.5 rounded-lg text-xs font-semibold ${currentMode === 'day' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-300 border border-slate-700'}">Día actual</button>
                        <button data-ia-mode="6m" class="px-3 py-1.5 rounded-lg text-xs font-semibold ${currentMode === '6m' ? 'bg-blue-600 text-white' : 'bg-slate-800 text-slate-300 border border-slate-700'}">Últimos 6 meses</button>
                    </div>
                    <div class="flex items-center gap-2 text-xs text-slate-400">
                        <span>Mín. trades</span>
                        <select id="iaMinTradesSelect" class="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-white">
                            <option value="2" ${currentMinTrades === 2 ? 'selected' : ''}>2</option>
                            <option value="5" ${currentMinTrades === 5 ? 'selected' : ''}>5</option>
                            <option value="10" ${currentMinTrades === 10 ? 'selected' : ''}>10</option>
                        </select>
                        <span class="ml-2">Confiable ≥10, muy confiable ≥20</span>
                    </div>
                    <div class="sm:ml-auto flex items-center gap-2 bg-slate-900/60 border border-slate-700 rounded-lg px-3 py-2">
                        <span class="text-xs text-slate-400">Score</span>
                        <span class="text-sm font-bold text-white">${score}</span>
                        <span class="text-[10px] px-2 py-0.5 rounded-full bg-blue-600/20 text-blue-300 border border-blue-500/30">${scoreLabel}</span>
                    </div>
                </div>

                <!-- TABS -->
                <div class="flex gap-2">
                    <button data-ia-tab="overview" class="ia-tab px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-800 text-white border border-slate-700">Resumen</button>
                    <button data-ia-tab="models" class="ia-tab px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-900 text-slate-300 border border-slate-800">📈 Modelos</button>
                    <button data-ia-tab="hotsetups" class="ia-tab px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-900 text-slate-300 border border-slate-800">🔥 Hot Setups</button>
                    <button data-ia-tab="insights" class="ia-tab px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-900 text-slate-300 border border-slate-800">Insights</button>
                    <button data-ia-tab="details" class="ia-tab px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-900 text-slate-300 border border-slate-800">Detalles</button>
                </div>

                <div data-ia-pane="overview" class="ia-pane space-y-3 sm:space-y-4">
                <!-- 1️⃣ RECOMENDACIÓN PRINCIPAL - GRANDE -->
                <div class="ia-main-card bg-gradient-to-br ${this.getAnalysisGradient(analysis.recommendation)} rounded-lg sm:rounded-xl p-4 sm:p-5 border-2 border-${this.getAnalysisBorder(analysis.recommendation)} shadow-2xl hover:shadow-2xl transition">
                    <div class="flex items-center gap-2 sm:gap-4 flex-col sm:flex-row">
                        <div class="flex-shrink-0">
                            ${this.getRecommendationSvg(analysis.recommendation)}
                        </div>
                        <div class="flex-1 text-center sm:text-left">
                            <h3 class="font-black text-white text-base sm:text-lg leading-tight">${analysis.message}</h3>
                            <p class="text-xs sm:text-sm text-gray-200 mt-2 leading-relaxed">${this.getRecommendationExplanation(analysis.recommendation, metrics)}</p>
                            <div class="mt-3 sm:mt-4 bg-black/30 rounded-lg p-2 sm:p-3 border border-white/10">
                                <div class="flex justify-between items-center mb-2 text-xs gap-2">
                                    <span class="text-gray-300 font-semibold">📊 CONFIANZA</span>
                                    <span class="text-white font-black text-base sm:text-lg">${analysis.confidence}%</span>
                                </div>
                                <div class="w-full bg-gray-700/40 rounded-full h-2 sm:h-3 overflow-hidden">
                                    <div class="bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 h-full rounded-full" style="width: ${analysis.confidence}%"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                ${canShowManagement ? `
                <div class="bg-slate-900/60 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-slate-700/60">
                    <div class="flex items-center gap-2 mb-2">
                        ${this.getIconSvg('chart', 'w-4 h-4 text-cyan-300')}
                        <h3 class="text-xs font-bold text-slate-200 uppercase">Gestión real (pips y tiempo)</h3>
                        <span class="text-[10px] text-slate-500">Solo cuentas sincronizadas</span>
                    </div>
                    <div class="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        <div class="bg-slate-800/60 rounded-lg p-2 border border-slate-700/40">
                            <p class="text-[10px] text-slate-400 uppercase">Pips Win</p>
                            <p class="text-sm font-bold text-emerald-400">${mgAvgWin}</p>
                        </div>
                        <div class="bg-slate-800/60 rounded-lg p-2 border border-slate-700/40">
                            <p class="text-[10px] text-slate-400 uppercase">Pips Loss</p>
                            <p class="text-sm font-bold text-red-400">-${mgAvgLoss}</p>
                        </div>
                        <div class="bg-slate-800/60 rounded-lg p-2 border border-slate-700/40">
                            <p class="text-[10px] text-slate-400 uppercase">R:R Real</p>
                            <p class="text-sm font-bold text-blue-300">${mgRR}</p>
                        </div>
                        <div class="bg-slate-800/60 rounded-lg p-2 border border-slate-700/40">
                            <p class="text-[10px] text-slate-400 uppercase">Hold (m)</p>
                            <p class="text-sm font-bold text-slate-200">${mgHoldWin} / ${mgHoldLoss}</p>
                        </div>
                    </div>
                    <p class="text-[10px] text-slate-500 mt-2">Muestra pips: ${management.pipsSample} trades · Muestra tiempo: ${management.durationSample} trades</p>
                </div>
                ` : ''}

                <div class="bg-slate-900/60 rounded-lg sm:rounded-xl p-3 sm:p-4 border border-slate-700/60">
                    <div class="flex items-center gap-2 mb-2">
                        ${this.getIconSvg('bulb', 'w-4 h-4 text-amber-300')}
                        <h3 class="text-xs font-bold text-slate-200 uppercase">${patternRecommendation.title}</h3>
                    </div>
                    <p class="text-[11px] text-slate-300 mb-2">${patternRecommendation.summary}</p>
                    <ul class="text-[11px] text-slate-200 space-y-1">
                        ${patternRecommendation.bullets.map(b => `<li>• ${b}</li>`).join('')}
                    </ul>
                </div>

                <!-- 2️⃣ MÉTRICAS CLAVE - 4 CARDS -->
                <div class="text-[11px] text-slate-400">
                    Muestra: se muestra desde 2 trades. Confiable ≥10, muy confiable ≥20.
                </div>
                <div>
                    <h3 class="text-xs font-bold text-gray-300 uppercase mb-2 flex items-center gap-2">
                        ${this.getIconSvg('chart', 'w-4 h-4 text-slate-300')}
                        <span>Métricas Clave</span>
                    </h3>
                    <div class="grid grid-cols-2 gap-2 sm:gap-3">
                        <div class="bg-slate-800/50 rounded-lg p-2 sm:p-3 border border-slate-700/50">
                            <p class="text-gray-400 text-xs uppercase">Win Rate</p>
                            <p class="text-lg sm:text-2xl font-bold text-blue-400 mt-1">${metrics.winRate.toFixed(0)}%</p>
                            <p class="text-xs text-gray-500 mt-1">
                                <span style="color: #4ade80;">W:${metrics.wins}</span> 
                                <span style="color: #f87171;">L:${metrics.losses}</span> 
                                <span style="color: #94a3b8;">BE:${metrics.beCount}</span>
                            </p>
                        </div>

                        <div class="bg-slate-800/50 rounded-lg p-2 sm:p-3 border border-slate-700/50">
                            <p class="text-gray-400 text-xs uppercase">Profit Factor</p>
                            <p class="text-lg sm:text-2xl font-bold text-purple-400 mt-1">${metrics.profitFactor.toFixed(2)}</p>
                            <p class="text-xs ${metrics.profitFactor > 1.5 ? 'text-emerald-400' : metrics.profitFactor > 1 ? 'text-amber-400' : 'text-red-400'} mt-1">
                                ${metrics.profitFactor > 1.5 ? '✅ Excelente' : metrics.profitFactor > 1 ? '⚠️ Bueno' : '❌ Bajo'}
                            </p>
                        </div>

                        <div class="bg-slate-800/50 rounded-lg p-2 sm:p-3 border border-slate-700/50">
                            <p class="text-gray-400 text-xs uppercase">Ganancia/Pérdida</p>
                            <p class="text-lg sm:text-2xl font-bold ${metrics.dayProfit >= 0 ? 'text-emerald-400' : 'text-red-400'} mt-1">
                                ${metrics.dayProfit >= 0 ? '+$' : '-$'}${Math.abs(metrics.dayProfit).toFixed(0)}
                            </p>
                            <p class="text-xs text-gray-500 mt-1">${metrics.dayProfitPercent >= 0 ? '+' : ''}${metrics.dayProfitPercent.toFixed(1)}%</p>
                        </div>

                        <div class="bg-slate-800/50 rounded-lg p-2 sm:p-3 border border-slate-700/50">
                            <p class="text-gray-400 text-xs uppercase">Trades</p>
                            <p class="text-lg sm:text-2xl font-bold text-emerald-400 mt-1">${metrics.analysisTrades}/<span class="text-gray-400">${metrics.totalTrades}</span></p>
                            <p class="text-xs text-gray-500 mt-1">DEL TOTAL</p>
                        </div>
                    </div>
                </div>

                ${canShowManagement ? `
                <div>
                    <h3 class="text-xs font-bold text-gray-300 uppercase mb-2 flex items-center gap-2">
                        ${this.getIconSvg('target', 'w-4 h-4 text-slate-300')}
                        <span>Gestión Real (Pips & Tiempo)</span>
                    </h3>
                    <div class="grid grid-cols-2 gap-2 sm:gap-3">
                        <div class="bg-slate-800/50 rounded-lg p-2 sm:p-3 border border-slate-700/50">
                            <p class="text-gray-400 text-xs uppercase">Pips ganados (avg)</p>
                            <p class="text-lg sm:text-2xl font-bold text-emerald-400 mt-1">${mgAvgWin}</p>
                            <p class="text-xs text-gray-500 mt-1">Muestra: ${management?.pipsSample || 0}</p>
                        </div>

                        <div class="bg-slate-800/50 rounded-lg p-2 sm:p-3 border border-slate-700/50">
                            <p class="text-gray-400 text-xs uppercase">Pips perdidos (avg)</p>
                            <p class="text-lg sm:text-2xl font-bold text-red-400 mt-1">${mgAvgLoss}</p>
                            <p class="text-xs text-gray-500 mt-1">R:R real ${mgRR}</p>
                        </div>

                        <div class="bg-slate-800/50 rounded-lg p-2 sm:p-3 border border-slate-700/50">
                            <p class="text-gray-400 text-xs uppercase">Tiempo ganador</p>
                            <p class="text-lg sm:text-2xl font-bold text-cyan-400 mt-1">${mgHoldWin} min</p>
                            <p class="text-xs text-gray-500 mt-1">Promedio</p>
                        </div>

                        <div class="bg-slate-800/50 rounded-lg p-2 sm:p-3 border border-slate-700/50">
                            <p class="text-gray-400 text-xs uppercase">Tiempo perdedor</p>
                            <p class="text-lg sm:text-2xl font-bold text-amber-400 mt-1">${mgHoldLoss} min</p>
                            <p class="text-xs text-gray-500 mt-1">Promedio</p>
                        </div>
                    </div>
                </div>

                <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                    <h4 class="text-xs font-bold text-slate-200 uppercase mb-2">${patternRecommendation.title}</h4>
                    <p class="text-xs text-slate-400 mb-2">${patternRecommendation.summary}</p>
                    <ul class="space-y-1">
                        ${patternRecommendation.bullets.map(b => `
                            <li class="text-xs text-slate-200">• ${b}</li>
                        `).join('')}
                    </ul>
                </div>
                ` : ''}

                </div>

                <!-- 🆕 PESTAÑA: MODEL ANALYSIS -->
                <div data-ia-pane="models" class="ia-pane hidden space-y-3 sm:space-y-4 max-h-[60vh] overflow-y-auto modal-scroll">
                    ${this.generateModelsAnalysisHTML(metrics.allTradesData)}
                </div>

                <!-- 🔥 PESTAÑA: HOT SETUPS (Phase 3) -->
                <div data-ia-pane="hotsetups" class="ia-pane hidden space-y-3 sm:space-y-4 max-h-[60vh] overflow-y-auto modal-scroll">
                    ${this.generateHotSetupsHTML(metrics.allTradesData)}
                </div>

                <div data-ia-pane="insights" class="ia-pane hidden space-y-3 sm:space-y-4">
                <!-- 7️⃣ KEY INSIGHTS -->
                <div>
                    <h3 class="text-xs font-bold text-gray-300 uppercase mb-2 flex items-center gap-2">
                        ${this.getIconSvg('bulb', 'w-4 h-4 text-slate-300')}
                        <span>Insights Clave</span>
                    </h3>
                    <div class="space-y-2">
                        ${analysis.keyInsights.map((insight, idx) => `
                            <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50 hover:border-blue-500/30 transition text-xs">
                                <p class="text-gray-200">✨ ${insight}</p>
                            </div>
                        `).join('')}
                    </div>
                </div>

                <!-- 3️⃣ ANÁLISIS DE PARES - DETALLADO -->
                <div>
                    <h3 class="text-xs font-bold text-gray-300 uppercase mb-2 flex items-center gap-2">
                        ${this.getIconSvg('diamond', 'w-4 h-4 text-slate-300')}
                        <span>Análisis de Pares</span>
                    </h3>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-3">
                        <div class="bg-emerald-900/30 rounded-lg p-3 border border-emerald-500/30">
                            <p class="text-emerald-400 text-xs font-bold uppercase mb-2">⭐ Mejor Par</p>
                            ${analysis.bestPair.hasData ? `
                            <p class="text-xl sm:text-2xl font-bold text-emerald-400">${analysis.bestPair.name}</p>
                            <div class="space-y-1 mt-2 text-xs">
                                <div class="flex justify-between"><span class="text-gray-400">Win Rate:</span><span class="text-emerald-400 font-bold">${analysis.bestPair.winRate}%</span></div>
                                <div class="flex justify-between"><span class="text-gray-400">Expectancy:</span><span class="text-emerald-400 font-bold">$${analysis.bestPair.expectancy}/trade</span></div>
                                <div class="flex justify-between"><span class="text-gray-400">Ganancia Avg:</span><span class="text-emerald-400 font-bold">$${analysis.bestPair.avgProfit}</span></div>
                                <div class="flex justify-between"><span class="text-gray-400">Trades:</span><span class="text-white font-bold">${analysis.bestPair.trades}</span></div>
                                <div class="flex justify-between"><span class="text-emerald-500 text-xs">Confiabilidad:</span><span class="text-emerald-300 text-xs font-semibold">${analysis.bestPair.sampleSize}</span></div>
                            </div>
                            <p class="text-gray-300 text-xs mt-2 pt-2 border-t border-emerald-500/20">${analysis.bestPair.reason}</p>
                            ` : `
                            <p class="text-sm text-slate-300">Sin datos suficientes para determinar el mejor par.</p>
                            `}
                        </div>

                        <div class="bg-red-900/30 rounded-lg p-3 border border-red-500/30">
                            <p class="text-red-400 text-xs font-bold uppercase mb-2">⚠️ Par a Evitar</p>
                            ${analysis.worstPair.hasData ? `
                            <p class="text-2xl font-bold text-red-400">${analysis.worstPair.name}</p>
                            <div class="space-y-1 mt-3 text-xs">
                                <div class="flex justify-between"><span class="text-gray-400">Win Rate:</span><span class="text-red-400 font-bold">${analysis.worstPair.winRate}%</span></div>
                                <div class="flex justify-between"><span class="text-gray-400">Expectancy:</span><span class="text-red-400 font-bold">$${analysis.worstPair.expectancy}/trade</span></div>
                                <div class="flex justify-between"><span class="text-gray-400">Pérdida Avg:</span><span class="text-red-400 font-bold">-$${analysis.worstPair.avgLoss}</span></div>
                                <div class="flex justify-between"><span class="text-gray-400">Trades:</span><span class="text-white font-bold">${analysis.worstPair.trades}</span></div>
                                <div class="flex justify-between"><span class="text-red-500 text-xs">Confiabilidad:</span><span class="text-red-300 text-xs font-semibold">${analysis.worstPair.sampleSize}</span></div>
                            </div>
                            <p class="text-gray-300 text-xs mt-3 pt-3 border-t border-red-500/20">${analysis.worstPair.reason}</p>
                            ` : `
                            <p class="text-sm text-slate-300">Sin datos suficientes para determinar el par a evitar.</p>
                            `}
                        </div>
                    </div>
                </div>

                <!-- 4️⃣ ANÁLISIS DE GANANCIAS/PÉRDIDAS -->
                <div class="grid grid-cols-2 gap-3">
                    <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                        <p class="text-emerald-400 text-xs font-bold uppercase mb-2">💰 Promedio Ganancia</p>
                        <p class="text-2xl font-bold text-emerald-400">$${metrics.avgWin.toFixed(0)}</p>
                        <p class="text-xs text-gray-400 mt-1">Por trade ganador</p>
                    </div>

                    <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                        <p class="text-red-400 text-xs font-bold uppercase mb-2">📉 Promedio Pérdida</p>
                        <p class="text-2xl font-bold text-red-400">$${Math.abs(metrics.avgLoss).toFixed(0)}</p>
                        <p class="text-xs text-gray-400 mt-1">Por trade perdedor</p>
                    </div>

                    <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                        <p class="text-blue-400 text-xs font-bold uppercase mb-2">⚖️ Risk/Reward</p>
                        <p class="text-2xl font-bold text-blue-400">${metrics.riskRewardRatio.toFixed(2)}</p>
                        <p class="text-xs text-gray-400 mt-1">${metrics.riskRewardRatio > 1 ? '✅ Favorable' : metrics.riskRewardRatio > 0.5 ? '⚠️ Aceptable' : '❌ Desfavorable'}</p>
                    </div>

                    <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                        <p class="text-cyan-400 text-xs font-bold uppercase mb-2">📊 Expectativa</p>
                        <p class="text-2xl font-bold text-cyan-400">$${(metrics.winRate/100 * metrics.avgWin - (100-metrics.winRate)/100 * Math.abs(metrics.avgLoss)).toFixed(0)}</p>
                        <p class="text-xs text-gray-400 mt-1">Ganancia esperada por trade</p>
                    </div>
                </div>

                <!-- 5️⃣ HORARIO ÓPTIMO -->
                <div>
                    <h3 class="text-xs font-bold text-gray-300 uppercase mb-2 flex items-center gap-2">
                        ${this.getIconSvg('clock', 'w-4 h-4 text-slate-300')}
                        <span>Análisis de Horario</span>
                    </h3>
                    <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                        <div class="grid grid-cols-2 gap-3">
                            <div>
                                <p class="text-blue-400 text-sm font-bold">Mejor Entrada</p>
                                <p class="text-2xl font-bold text-blue-400 mt-1">${metrics.bestHour ? metrics.bestHour.time : 'N/A'}</p>
                                <div class="text-xs text-gray-400 mt-1 space-y-1">
                                    <p>Win Rate: ${metrics.bestHour ? metrics.bestHour.winRate.toFixed(0) : '0'}%</p>
                                    <p>Expectancy: ${metrics.bestHour ? `$${metrics.bestHour.expectancy.toFixed(0)}/trade` : 'N/A'}</p>
                                    <p class="text-blue-500">${metrics.bestHour ? metrics.bestHour.sampleSize : 'SIN DATOS'}</p>
                                </div>
                            </div>
                            <div>
                                <p class="text-gray-400 text-sm font-bold">Hora Actual</p>
                                <p class="text-2xl font-bold text-white mt-1">${analysis.timeAnalysis.currentTime}</p>
                                <p class="text-xs ${analysis.timeAnalysis.isOptimal ? 'text-emerald-400' : 'text-amber-400'} mt-1">
                                    ${analysis.timeAnalysis.isOptimal ? '✅ Óptimo' : '⚠️ No óptimo'}
                                </p>
                            </div>
                        </div>
                        ${metrics.bestHour ? `
                            <div class="mt-3 p-2 bg-blue-900/20 rounded border border-blue-500/20 text-xs text-blue-200">
                                Tu mejor entrada es a las ${metrics.bestHour.time}. Expectancy: $${metrics.bestHour.expectancy.toFixed(0)}/trade en ${metrics.bestHour.count} operaciones.
                            </div>
                        ` : ''}
                    </div>
                </div>

                </div>

                <div data-ia-pane="details" class="ia-pane hidden space-y-3 sm:space-y-4">
                <!-- 0️⃣ NOTICIAS ECONÓMICAS (IMPACTO) -->
                <div>
                    <h3 class="text-xs font-bold text-gray-300 uppercase mb-2 flex items-center gap-2">
                        ${this.getIconSvg('warning', 'w-4 h-4 text-yellow-300')}
                        <span>Impacto de Noticias (${metrics.newsImpactByDay?.dayName || '—'})</span>
                    </h3>
                    <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                        <div class="grid grid-cols-2 gap-3">
                            <div class="bg-slate-900/60 rounded-lg p-3 border border-slate-700">
                                <p class="text-gray-300 text-xs font-semibold">Impacto MEDIO</p>
                                <p class="text-2xl font-bold text-yellow-300 mt-1">
                                    ${metrics.newsImpactByDay?.hasData && metrics.newsImpactByDay.medium.trades > 0 ? `${metrics.newsImpactByDay.medium.winRate.toFixed(0)}%` : 'N/A'}
                                </p>
                                <p class="text-[11px] text-slate-500 mt-1">
                                    ${metrics.newsImpactByDay?.hasData ? `${metrics.newsImpactByDay.medium.trades} trades · ${metrics.newsImpactByDay.medium.days} días` : 'Solo UNLIMITED'}
                                </p>
                                <p class="text-[11px] text-slate-500 mt-1">
                                    Profit diario: ${metrics.newsImpactByDay?.hasData ? `$${metrics.newsImpactByDay.medium.avgDayProfit.toFixed(2)}` : '—'}
                                </p>
                            </div>
                            <div class="bg-slate-900/60 rounded-lg p-3 border border-slate-700">
                                <p class="text-gray-300 text-xs font-semibold">Impacto ALTO</p>
                                <p class="text-2xl font-bold text-red-400 mt-1">
                                    ${metrics.newsImpactByDay?.hasData && metrics.newsImpactByDay.high.trades > 0 ? `${metrics.newsImpactByDay.high.winRate.toFixed(0)}%` : 'N/A'}
                                </p>
                                <p class="text-[11px] text-slate-500 mt-1">
                                    ${metrics.newsImpactByDay?.hasData ? `${metrics.newsImpactByDay.high.trades} trades · ${metrics.newsImpactByDay.high.days} días` : 'Solo UNLIMITED'}
                                </p>
                                <p class="text-[11px] text-slate-500 mt-1">
                                    Profit diario: ${metrics.newsImpactByDay?.hasData ? `$${metrics.newsImpactByDay.high.avgDayProfit.toFixed(2)}` : '—'}
                                </p>
                            </div>
                        </div>
                        <p class="text-[11px] text-slate-400 mt-3 pt-3 border-t border-slate-700">
                            <strong>Qué estamos midiendo:</strong> solo los ${metrics.newsImpactByDay?.dayName || 'días iguales'} que tuvieron noticias de impacto.
                            Calculamos el win rate y el profit total del día para decir si esos días suelen ser buenos o malos.
                        </p>
                        <p class="text-[11px] text-slate-400 mt-2">
                            <strong>Insight:</strong>
                            ${metrics.newsImpactByDay?.hasData ? (
                                (metrics.newsImpactByDay.high.days === 0 && metrics.newsImpactByDay.medium.days === 0)
                                    ? 'No hay suficientes días con noticias para ese día de la semana.'
                                    : `
                                    ${metrics.newsImpactByDay.high.days > 0 && (metrics.newsImpactByDay.high.winRate < 50 || metrics.newsImpactByDay.high.avgDayProfit < 0)
                                        ? 'En noticias de impacto ALTO, tu rendimiento suele ser negativo. Lo ideal es reducir riesgo o evitar operar.'
                                        : metrics.newsImpactByDay.high.days > 0
                                            ? 'En noticias de impacto ALTO, tu rendimiento es aceptable. Puedes operar, pero con control de riesgo.'
                                            : 'No hay suficientes días de impacto ALTO para concluir.'}
                                    ${metrics.newsImpactByDay.medium.days > 0 && (metrics.newsImpactByDay.medium.winRate < 50 || metrics.newsImpactByDay.medium.avgDayProfit < 0)
                                        ? 'En impacto MEDIO, tu rendimiento es débil. Sé más selectivo en esas sesiones.'
                                        : metrics.newsImpactByDay.medium.days > 0
                                            ? 'En impacto MEDIO, tu rendimiento es razonable. Mantén el plan.'
                                            : 'No hay suficientes días de impacto MEDIO para concluir.'}
                                    `.trim()
                            ) : 'Solo disponible en plan UNLIMITED.'}
                        </p>
                    </div>
                </div>

                <!-- 6️⃣ RACHA Y TENDENCIA -->
                <div>
                    <h3 class="text-xs font-bold text-gray-300 uppercase mb-2 flex items-center gap-2">
                        ${this.getIconSvg('refresh', 'w-4 h-4 text-slate-300')}
                        <span>Racha y Tendencia Semanal</span>
                    </h3>
                    
                    <!-- RACHA ACTUAL -->
                    <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50 mb-3">
                        <!-- HISTÓRICO DE RACHAS DEL DÍA -->
                        <div class="grid grid-cols-2 gap-3 mb-3">
                            <div class="bg-emerald-900/30 rounded-lg p-3 border border-emerald-500/30">
                                <p class="text-xs font-bold text-emerald-400 mb-2">✅ Máx Racha Ganadora</p>
                                <p class="text-3xl font-bold text-emerald-400">${analysis.streakAnalysis.longestWin}</p>
                                <p class="text-xs text-gray-400 mt-1">consecutivos</p>
                            </div>
                            <div class="bg-red-900/30 rounded-lg p-3 border border-red-500/30">
                                <p class="text-xs font-bold text-red-400 mb-2">❌ Máx Racha Perdedora</p>
                                <p class="text-3xl font-bold text-red-400">${analysis.streakAnalysis.longestLoss}</p>
                                <p class="text-xs text-gray-400 mt-1">consecutivos</p>
                            </div>
                        </div>
                        
                        <div class="p-3 bg-yellow-900/20 rounded-lg border border-yellow-500/20">
                            <p class="text-xs font-bold text-yellow-400 mb-1">Recomendación:</p>
                            <p class="text-xs text-yellow-100">${analysis.streakAnalysis.recommendation}</p>
                        </div>
                    </div>

                    <!-- ANÁLISIS DE RATIOS POR DÍA -->
                    <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50 mt-3">
                        <p class="text-xs font-bold text-gray-300 mb-3">💰 Análisis de Ratios - ${metrics.dayName} (${metrics.analysisTrades} trades)</p>
                        ${this.generateRatiosTable(this.analyzeRatiosByDay(metrics.analysisTradesData, metrics.initialBalance))}
                        <p class="text-xs text-gray-400 mt-3 pt-3 border-t border-slate-700">
                            <strong>Cómo leer:</strong> 1:1 = RR 1:1 | 1:2 = RR 1:2 | 1:3 = RR 1:3 | 1:4+ = RR >= 1:4 | Ganancia = P/L total en ese ratio
                        </p>
                    </div>
                </div>

                <!-- 6.5️⃣ DISCIPLINA DE RIESGO -->
                <div>
                    <h3 class="text-xs font-bold text-gray-300 uppercase mb-2 flex items-center gap-2">
                        ${this.getIconSvg('warning', 'w-4 h-4 text-amber-300')}
                        <span>Disciplina de Riesgo</span>
                    </h3>
                    <div class="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                        ${impulse.hasLotData ? `
                        <div class="grid grid-cols-2 gap-3 text-xs">
                            <div class="bg-slate-900/60 rounded-lg p-3 border border-slate-700">
                                <p class="text-gray-200 font-semibold">Escaladas de riesgo</p>
                                <p class="text-[11px] text-slate-500">Trades donde el lote sube ≥1.5x vs el anterior</p>
                                <p class="text-lg font-bold ${impulse.escalations > 0 ? 'text-amber-400' : 'text-emerald-400'}">${impulse.escalations}</p>
                                <p class="text-[11px] text-slate-500">Máx aumento: ${impulse.maxIncrease.toFixed(2)}x</p>
                            </div>
                            <div class="bg-slate-900/60 rounded-lg p-3 border border-slate-700">
                                <p class="text-gray-200 font-semibold">Escalada tras pérdida</p>
                                <p class="text-[11px] text-slate-500">Subes tamaño después de un trade negativo</p>
                                <p class="text-lg font-bold ${impulse.lossEscalations > 0 ? 'text-red-400' : 'text-emerald-400'}">${impulse.lossEscalations}</p>
                                <p class="text-[11px] text-slate-500">Señal de revenge</p>
                            </div>
                            <div class="bg-slate-900/60 rounded-lg p-3 border border-slate-700">
                                <p class="text-gray-200 font-semibold">Revenge rápido</p>
                                <p class="text-[11px] text-slate-500">Nuevas entradas ≤15 min después de una pérdida</p>
                                <p class="text-lg font-bold ${impulse.revengeCount > 0 ? 'text-red-400' : 'text-emerald-400'}">${impulse.hasTimeData ? impulse.revengeCount : 'N/A'}</p>
                                <p class="text-[11px] text-slate-500">${impulse.hasTimeData ? 'Cuenta trades impulsivos' : 'Sin horas registradas'}</p>
                            </div>
                            <div class="bg-slate-900/60 rounded-lg p-3 border border-slate-700">
                                <p class="text-gray-200 font-semibold">Consistencia de lotes</p>
                                <p class="text-[11px] text-slate-500">Variación del tamaño entre trades</p>
                                <p class="text-lg font-bold text-blue-400">${impulse.consistencyLabel}</p>
                                <p class="text-[11px] text-slate-500">Promedio: ${impulse.avgLot.toFixed(2)} lot</p>
                            </div>
                        </div>
                        <div class="mt-3 text-[11px] text-slate-400">
                            Umbral de alerta: aumentos ≥1.5x y re-entrada rápida tras pérdida.
                        </div>
                        ` : `
                        <p class="text-xs text-slate-300">Sin datos de lotes suficientes para detectar impulsividad.</p>
                        `}
                    </div>
                </div>

                </div>

                <!-- CLOSE BUTTON -->
                <button onclick="window.iaScannerSAAS.close()" class="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-2 rounded-lg transition text-sm">
                    ✓ Cerrar Análisis
                </button>
            </div>
        `;
        this.bindUI();
    }

    bindUI() {
        const tabs = this.modal.querySelectorAll('.ia-tab');
        const panes = this.modal.querySelectorAll('.ia-pane');
        if (tabs.length && panes.length) {
            const savedTab = localStorage.getItem('iaScannerTab') || 'overview';
            const setActiveTab = (tabId) => {
                tabs.forEach(btn => {
                    const active = btn.getAttribute('data-ia-tab') === tabId;
                    btn.classList.toggle('bg-slate-800', active);
                    btn.classList.toggle('text-white', active);
                    btn.classList.toggle('border-slate-700', active);
                    btn.classList.toggle('bg-slate-900', !active);
                    btn.classList.toggle('text-slate-300', !active);
                    btn.classList.toggle('border-slate-800', !active);
                });
                panes.forEach(pane => {
                    pane.classList.toggle('hidden', pane.getAttribute('data-ia-pane') !== tabId);
                });
                localStorage.setItem('iaScannerTab', tabId);
            };

            tabs.forEach(btn => btn.addEventListener('click', () => setActiveTab(btn.getAttribute('data-ia-tab'))));
            setActiveTab(savedTab);
        }

        this.modal.querySelectorAll('[data-ia-mode]').forEach(btn => {
            btn.addEventListener('click', () => {
                const mode = btn.getAttribute('data-ia-mode');
                localStorage.setItem('iaScannerMode', mode);
                
                // 🔄 Si selecciona "6m", recarga el histórico completo antes de renderizar
                if (mode === '6m') {
                    this.loadFullHistory().then(() => {
                        this.render();
                    });
                } else {
                    this.render();
                }
            });
        });

        const minSelect = this.modal.querySelector('#iaMinTradesSelect');
        if (minSelect) {
            minSelect.addEventListener('change', () => {
                localStorage.setItem('iaScannerMinTrades', minSelect.value);
                this.render();
            });
        }
    }

    /**
     * Formatea fecha YYYY-MM-DD a DD/MM/YYYY para el header
     */
    formatDateForHeader(dateStr) {
        if (!dateStr || !dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
        const [year, month, day] = dateStr.split('-');
        return `${day}/${month}/${year}`;
    }

    getRecommendationExplanation(recommendation, metrics) {
        const explanations = {
            EXCELLENT: `Con un win rate del ${metrics.winRate.toFixed(0)}% y profit factor de ${metrics.profitFactor.toFixed(2)}, las condiciones son óptimas para operar. Tus estrategias están funcionando muy bien.`,
            GOOD: `Estás operando consistentemente en positivo. Mantén tu disciplina y continúa ejecutando tu plan de trading sin cambios drásticos.`,
            CAUTION: `Tienes una racha ganadora en progreso, pero mantén los pies en tierra. No aumentes riesgo ni cambies tu plan por la euforia.`,
            WARNING: `Tu win rate está baja (${metrics.winRate.toFixed(0)}%). Necesitas revisar tu estrategia de entrada y aplicar mejoras.`,
            NEUTRAL: `Las condiciones son moderadas. Opera con disciplina y enfócate en setups de alta calidad.`
        };
        return explanations[recommendation] || explanations.NEUTRAL;
    }

    calculateScore(metrics) {
        let score = 0;

        // Win Rate (0-35)
        score += Math.min(35, Math.max(0, metrics.winRate) * 0.35);

        // Profit Factor (0-25)
        score += Math.min(25, Math.max(0, metrics.profitFactor) * 10);

        // Risk/Reward (0-20)
        score += Math.min(20, Math.max(0, metrics.riskRewardRatio) * 8);

        // Day profit (0-10)
        if (metrics.dayProfit > 0) score += 10;

        // Streak adjustment (-5 to +5)
        if (metrics.currentStreak.type === 'WIN' && metrics.currentStreak.consecutive >= 2) score += 5;
        if (metrics.currentStreak.type === 'LOSS' && metrics.currentStreak.consecutive >= 2) score -= 5;

        return Math.max(0, Math.min(100, Math.round(score)));
    }

    getScoreLabel(score) {
        if (score >= 80) return 'EXCELENTE';
        if (score >= 65) return 'BUENA';
        if (score >= 50) return 'MEDIA';
        return 'BAJA';
    }

    getTopRecommendations(metrics) {
        const recs = [];
        if (metrics.winRate < 45) recs.push('Reduce entradas y prioriza setups A+');
        if (metrics.riskRewardRatio < 1) recs.push('Mejora RR: ajusta SL/TP y evita trades sin margen');
        if (metrics.dayProfit < 0) recs.push('Limita pérdidas: reduce tamaño o pausa si hay racha negativa');
        if (metrics.bestHour) recs.push(`Opera en tu mejor horario: ${metrics.bestHour.time}`);
        if (metrics.bestPair?.pair) recs.push(`Enfócate en ${metrics.bestPair.pair} (mejor expectancy)`);
        return recs.slice(0, 3);
    }

    getWarnings(metrics) {
        const warnings = [];
        if (metrics.winRate < 40) warnings.push('Win rate muy bajo hoy.');
        if (metrics.dayProfit < 0 && metrics.currentStreak.type === 'LOSS') warnings.push('Racha negativa activa: pausa o reduce riesgo.');
        if (metrics.riskRewardRatio < 0.7) warnings.push('RR desfavorable: ajusta SL/TP.');
        return warnings;
    }

    analyzeImpulseBehavior(trades) {
        const normalized = trades.map(t => {
            const lot = this.getTradeLotSize(t);
            const profit = parseFloat(t.profitUSD) || 0;
            const ts = this.getTradeTimestamp(t);
            return { ...t, lot, profit, ts };
        }).filter(t => Number.isFinite(t.lot) && t.lot > 0);

        if (normalized.length < 2) {
            return {
                hasLotData: false,
                escalations: 0,
                lossEscalations: 0,
                revengeCount: 0,
                maxIncrease: 1,
                avgLot: 0,
                consistencyLabel: 'N/A',
                hasTimeData: false
            };
        }

        const sorted = [...normalized].sort((a, b) => {
            if (a.ts && b.ts) return a.ts - b.ts;
            const da = a.date || a.trade_date || '';
            const db = b.date || b.trade_date || '';
            return String(da).localeCompare(String(db));
        });

        const lots = sorted.map(t => t.lot);
        const avgLot = lots.reduce((s, v) => s + v, 0) / lots.length;
        const variance = lots.reduce((s, v) => s + Math.pow(v - avgLot, 2), 0) / lots.length;
        const stdLot = Math.sqrt(variance);
        const cv = avgLot > 0 ? stdLot / avgLot : 0;
        const consistencyLabel = cv < 0.2 ? 'ALTA' : cv < 0.5 ? 'MEDIA' : 'BAJA';

        let escalations = 0;
        let lossEscalations = 0;
        let revengeCount = 0;
        let maxIncrease = 1;
        let hasTimeData = false;

        for (let i = 1; i < sorted.length; i++) {
            const prev = sorted[i - 1];
            const curr = sorted[i];
            if (!prev.lot || !curr.lot) continue;

            const ratio = curr.lot / prev.lot;
            if (ratio > maxIncrease) maxIncrease = ratio;

            if (ratio >= 1.5) {
                escalations++;
                if (prev.profit < 0) lossEscalations++;
            }

            if (prev.profit < 0 && prev.ts && curr.ts) {
                hasTimeData = true;
                const diff = curr.ts - prev.ts;
                if (diff > 0 && diff <= 15 * 60 * 1000) revengeCount++;
            }
        }

        return {
            hasLotData: true,
            escalations,
            lossEscalations,
            revengeCount,
            maxIncrease: Math.max(1, maxIncrease),
            avgLot,
            consistencyLabel,
            hasTimeData
        };
    }

    getTradeLotSize(trade) {
        const raw = trade?.lotSize ?? trade?.lot_size ?? trade?.volume ?? trade?.lot;
        const val = parseFloat(raw);
        return Number.isFinite(val) ? val : null;
    }

    getTradeTimestamp(trade) {
        const dateStr = trade?.date || trade?.trade_date;
        const timeStr = trade?.entryTime;
        if (dateStr && timeStr) {
            const ts = new Date(`${dateStr}T${timeStr}:00`).getTime();
            if (!Number.isNaN(ts)) return ts;
        }
        if (trade?.timestamp) {
            const ts = new Date(trade.timestamp).getTime();
            if (!Number.isNaN(ts)) return ts;
        }
        if (dateStr) {
            const ts = new Date(dateStr).getTime();
            if (!Number.isNaN(ts)) return ts;
        }
        return null;
    }

    generateAIAnalysis(metrics) {
        const now = new Date();
        const dayOfWeek = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'][now.getDay()];
        const hour = String(now.getHours()).padStart(2, '0');
        const minutes = String(now.getMinutes()).padStart(2, '0');

        return {
            // Recomendación global
            recommendation: this.getRecommendationStatus(metrics),
            icon: this.getRecommendationIcon(metrics),
            message: this.getRecommendationMessage(metrics),
            confidence: this.calculateConfidence(metrics),

            // Análisis de pares
            bestPair: {
                hasData: !!metrics.bestPair,
                name: metrics.bestPair?.pair || 'Sin datos',
                winRate: metrics.bestPair ? metrics.bestPair.winRate.toFixed(0) : '0',
                avgProfit: metrics.bestPair ? (metrics.bestPair.profit / (metrics.bestPair.count || 1)).toFixed(2) : '0',
                expectancy: metrics.bestPair ? metrics.bestPair.expectancy.toFixed(2) : '0',
                trades: metrics.bestPair?.count || 0,
                sampleSize: metrics.bestPair?.count
                    ? (metrics.bestPair.count >= 20 ? 'MUY CONFIABLE' : metrics.bestPair.count >= 10 ? 'CONFIABLE' : 'MUESTRA PEQUEÑA')
                    : 'SIN DATOS',
                reason: metrics.bestPair ? this.getBestPairReason(metrics.bestPair) : 'Sin datos suficientes'
            },
            worstPair: {
                hasData: !!metrics.worstPair,
                name: metrics.worstPair?.pair || 'Sin datos',
                winRate: metrics.worstPair ? metrics.worstPair.winRate.toFixed(0) : '0',
                avgLoss: metrics.worstPair ? Math.abs((metrics.worstPair.profit / (metrics.worstPair.count || 1)).toFixed(2)) : '0',
                expectancy: metrics.worstPair ? metrics.worstPair.expectancy.toFixed(2) : '0',
                trades: metrics.worstPair?.count || 0,
                sampleSize: metrics.worstPair?.count
                    ? (metrics.worstPair.count >= 20 ? 'MUY CONFIABLE' : metrics.worstPair.count >= 10 ? 'CONFIABLE' : 'MUESTRA PEQUEÑA')
                    : 'SIN DATOS',
                reason: metrics.worstPair ? this.getWorstPairReason(metrics.worstPair) : 'Sin datos suficientes'
            },

            // Análisis del día
            dayAnalysis: {
                dayOfWeek: metrics.dayName,
                historicalWR: metrics.winRate.toFixed(0),
                tradesAnalyzed: metrics.analysisTrades,
                status: metrics.winRate > 50 ? 'positive' : 'negative'
            },

            // Análisis de horario
            timeAnalysis: {
                bestHour: metrics.bestHour ? metrics.bestHour.time : 'N/A',
                hourWR: metrics.bestHour?.winRate.toFixed(0) || '0',
                expectancy: metrics.bestHour?.expectancy.toFixed(2) || '0',
                trades: metrics.bestHour?.count || 0,
                sampleSize: metrics.bestHour?.sampleSize || 'SIN DATOS',
                currentTime: `${hour}:${minutes}`,
                isOptimal: metrics.bestHour ? this.isCurrentHourOptimal(metrics.bestHour.time) : false
            },

            // Análisis de racha
            streakAnalysis: {
                consecutive: metrics.currentStreak.consecutive,
                current: metrics.currentStreak.type,
                pattern: this.getStreakPattern(metrics),
                recommendation: this.getStreakRecommendation(metrics),
                allWinStreaks: metrics.allDayStreaks.wins,
                allLossStreaks: metrics.allDayStreaks.losses,
                longestWin: metrics.allDayStreaks.longestWinStreak,
                longestLoss: metrics.allDayStreaks.longestLossStreak,
                totalStreaksCount: (metrics.allDayStreaks.wins.length + metrics.allDayStreaks.losses.length)
            },

            // Key Insights
            keyInsights: this.getKeyInsights(metrics)
        };
    }

    getRecommendationStatus(metrics) {
        // IMPORTANTE: Usar SOLO dayProfit (del día), no totalProfitPercent (cuenta completa)
        
        // EXCELENTE: WR alto + Ganancia + Expectancy positiva
        if (metrics.dayProfit > 0 && metrics.winRate >= 60 && metrics.riskRewardRatio > 1.5) {
            return 'EXCELLENT';
        }
        
        // WARNING: WR bajo O perdiendo dinero significativamente
        if (metrics.winRate < 40 || metrics.dayProfit < -500) {
            return 'WARNING';
        }
        
        // CAUTION: Racha ganadora fuerte (cuidado con sobreconfianza)
        if (metrics.currentStreak.consecutive > 3 && metrics.currentStreak.type === 'WIN') {
            return 'CAUTION';
        }
        
        // GOOD: Ganancia + WR decente (sin ser excelente)
        if (metrics.dayProfit > 0 && metrics.winRate >= 50) {
            return 'GOOD';
        }
        
        // NEUTRAL: Otros casos
        return 'NEUTRAL';
    }

    getRecommendationIcon(metrics) {
        const status = this.getRecommendationStatus(metrics);
        const icons = {
            EXCELLENT: '🚀',
            GOOD: '✅',
            CAUTION: '🔥',
            WARNING: '⚠️',
            NEUTRAL: '⚡'
        };
        return icons[status] || '⚡';
    }

    getRecommendationSvg(status) {
        const color = {
            EXCELLENT: 'text-emerald-400',
            GOOD: 'text-blue-400',
            CAUTION: 'text-amber-400',
            WARNING: 'text-red-400',
            NEUTRAL: 'text-slate-300'
        }[status] || 'text-slate-300';

        return `
            <div class="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-slate-900/60 border border-slate-700/60 flex items-center justify-center shadow-xl">
                ${this.getIconSvg('spark', `w-8 h-8 ${color}`)}
            </div>
        `;
    }

    getIconSvg(name, className = '') {
        const cls = `"${className}"`;
        switch (name) {
            case 'bolt':
                return `<svg class=${cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M13 2L3 14h7l-1 8 12-16h-7l1-4z"/></svg>`;
            case 'target':
                return `<svg class=${cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><circle cx="12" cy="12" r="5"/><path d="M12 7v2M17 12h-2M12 17v-2M7 12h2"/></svg>`;
            case 'chart':
                return `<svg class=${cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3v18h18"/><rect x="7" y="13" width="3" height="6"/><rect x="12" y="9" width="3" height="10"/><rect x="17" y="6" width="3" height="13"/></svg>`;
            case 'diamond':
                return `<svg class=${cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l5 6-5 14-5-14 5-6z"/><path d="M7 8h10"/></svg>`;
            case 'clock':
                return `<svg class=${cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v6l4 2"/></svg>`;
            case 'refresh':
                return `<svg class=${cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 0 0-9-9 9 9 0 0 0-6.36 2.64"/><path d="M3 4v5h5"/><path d="M3 12a9 9 0 0 0 9 9 9 9 0 0 0 6.36-2.64"/><path d="M21 20v-5h-5"/></svg>`;
            case 'warning':
                return `<svg class=${cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l9 16H3l9-16z"/><path d="M12 9v4"/><path d="M12 17h.01"/></svg>`;
            case 'bulb':
                return `<svg class=${cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18h6"/><path d="M10 22h4"/><path d="M12 2a7 7 0 0 0-4 12c.5.5 1 1.5 1 2h6c0-.5.5-1.5 1-2a7 7 0 0 0-4-12z"/></svg>`;
            case 'spark':
                return `<svg class=${cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2l2.5 5 5 2.5-5 2.5-2.5 5-2.5-5-5-2.5 5-2.5L12 2z"/><path d="M19 15l1 2 2 1-2 1-1 2-1-2-2-1 2-1 1-2z"/></svg>`;
            default:
                return `<svg class=${cls} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/></svg>`;
        }
    }

    getRecommendationMessage(metrics) {
        const status = this.getRecommendationStatus(metrics);
        
        // Analizar problemas específicos
        const wrGood = metrics.winRate >= 50;
        const profitGood = metrics.dayProfit > 0;
        const rrGood = metrics.riskRewardRatio > 1.5;
        const avgWin = Math.abs(metrics.avgWin);
        const avgLoss = Math.abs(metrics.avgLoss);
        const lossTooBig = avgLoss > avgWin * 1.5;
        
        // CASO ESPECÍFICO: WR alto pero pérdida (como tu Jueves)
        if (wrGood && !profitGood && lossTooBig) {
            return `⚠️ PROBLEMA DE RIESGO: Win Rate ${metrics.winRate.toFixed(0)}% pero pierdes $${Math.abs(metrics.dayProfit).toFixed(0)}. Tus pérdidas (${avgLoss.toFixed(0)}) son MUCHO MAYORES que tus ganancias (${avgWin.toFixed(0)}). Necesitas mejorar tu gestión de stop loss.`;
        }
        
        // WR alto pero pequeña pérdida
        if (wrGood && !profitGood && !lossTooBig) {
            return `⚡ JUEVES DIFÍCIL: Win Rate ${metrics.winRate.toFixed(0)}% es bueno, pero hoy perdiste $${Math.abs(metrics.dayProfit).toFixed(0)}. Algunos trades grandes se fueron en contra. Revisa si fue setup incorrecto o mercado adverso.`;
        }
        
        // WR bajo + pérdida
        if (!wrGood && !profitGood) {
            return `🛑 DÍA DIFÍCIL: Win Rate solo ${metrics.winRate.toFixed(0)}% y pérdida de $${Math.abs(metrics.dayProfit).toFixed(0)}. Tomas demasiadas entradas débiles. SÉ MÁS SELECTIVO con tus setups.`;
        }
        
        // Status base
        const messages = {
            EXCELLENT: `🚀 EXCELENTE: Win Rate ${metrics.winRate.toFixed(0)}%, ganancia de $${metrics.dayProfit.toFixed(0)}, RR ${metrics.riskRewardRatio.toFixed(1)}:1. Hoy estás en la zona óptima.`,
            GOOD: `✅ FAVORABLE: Ganancia de $${metrics.dayProfit.toFixed(0)} con Win Rate ${metrics.winRate.toFixed(0)}%. Mantén la disciplina, no cambies nada.`,
            CAUTION: `🔥 RACHA GANADORA: ${metrics.currentStreak.consecutive} victorias seguidas. CUIDADO: el trading emocional es el enemigo. No aumentes riesgo ni abandones tu plan.`,
            WARNING: `⚠️ ALERTA: Condiciones adversas. Win Rate ${metrics.winRate.toFixed(0)}%, pérdida $${Math.abs(metrics.dayProfit).toFixed(0)}. Reduce tamaño de posición y espera a setups de ALTA calidad.`,
            NEUTRAL: `⚡ MODERADO: WR ${metrics.winRate.toFixed(0)}%, P&L $${metrics.dayProfit.toFixed(0)}. Día típico. Mantén disciplina sin cambios drásticos.`
        };
        
        return messages[status] || 'Analiza las métricas antes de operar';
    }

    calculateConfidence(metrics) {
        let score = 0;
        
        // 1️⃣ EXPECTANCY (30 puntos max)
        const expectancy = metrics.bestPair?.expectancy || 0;
        let expectancyScore = 0;
        if (metrics.dayProfit > 0) {  // IMPORTANTE: usar dayProfit, no account total
            if (expectancy > 25) expectancyScore = 30;
            else if (expectancy > 10) expectancyScore = 20;
            else if (expectancy > 0) expectancyScore = 10;
            else expectancyScore = 0;
        } else {
            expectancyScore = 0;  // Si pierdes dinero = 0 confianza en expectancy
        }
        score += expectancyScore;
        
        // 2️⃣ VOLUMEN (25 puntos max)
        let volumeScore = 0;
        if (metrics.analysisTrades > 15) volumeScore = 25;
        else if (metrics.analysisTrades > 10) volumeScore = 18;
        else if (metrics.analysisTrades > 5) volumeScore = 10;
        else volumeScore = 5;
        score += volumeScore;
        
        // 3️⃣ RATIO GANANCIA/PÉRDIDA (20 puntos max)
        // Comparar ganancia promedio vs pérdida promedio
        const avgWin = Math.abs(metrics.avgWin);
        const avgLoss = Math.abs(metrics.avgLoss);
        let ratioScore = 0;
        if (avgWin > avgLoss * 1.2) ratioScore = 20;  // Win significativamente mayor
        else if (avgWin >= avgLoss * 0.8) ratioScore = 10;  // Balanceado
        else ratioScore = 0;  // Loss es mucho mayor
        score += ratioScore;
        
        // 4️⃣ RACHA (15 puntos max)
        let streakScore = 0;
        if (metrics.currentStreak.type === 'WIN' && metrics.currentStreak.consecutive >= 3) {
            streakScore = 15;
        } else if (metrics.currentStreak.type === 'WIN' && metrics.currentStreak.consecutive >= 1) {
            streakScore = 8;
        } else if (metrics.currentStreak.type === 'LOSS') {
            streakScore = 0;
        } else {
            streakScore = 5;  // Alternando
        }
        score += streakScore;
        
        // 5️⃣ WIN RATE (10 puntos max)
        let wrScore = 0;
        if (metrics.winRate > 60) wrScore = 10;
        else if (metrics.winRate > 50) wrScore = 6;
        else if (metrics.winRate > 40) wrScore = 3;
        else wrScore = 0;
        score += wrScore;
        
        return Math.min(100, Math.round(score));
    }

    getAnalysisGradient(status) {
        const gradients = {
            EXCELLENT: 'from-emerald-950/40 to-slate-900/40',
            GOOD: 'from-blue-950/40 to-slate-900/40',
            CAUTION: 'from-yellow-950/40 to-slate-900/40',
            WARNING: 'from-red-950/40 to-slate-900/40',
            NEUTRAL: 'from-purple-950/40 to-slate-900/40'
        };
        return gradients[status] || gradients.NEUTRAL;
    }

    getAnalysisBorder(status) {
        const borders = {
            EXCELLENT: 'emerald-500/50',
            GOOD: 'blue-500/50',
            CAUTION: 'yellow-500/50',
            WARNING: 'red-500/50',
            NEUTRAL: 'purple-500/50'
        };
        return borders[status] || borders.NEUTRAL;
    }

    getBestPairReason(pair) {
        if (!pair) return 'Sin datos suficientes';
        if (pair.count < 5) return `⚠️ Muestra pequeña (${pair.count} trades). Espera más datos.`;
        if (pair.expectancy > 50) return `✅ Excelente expectancy ($${pair.expectancy.toFixed(0)}/trade). Tu zona de confort.`;
        if (pair.expectancy > 20) return `✅ Buena expectancy ($${pair.expectancy.toFixed(0)}/trade). Aumenta volumen aquí.`;
        if (pair.expectancy > 0) return `✓ Expectancy positiva ($${pair.expectancy.toFixed(0)}/trade). Continúa operando.`;
        return `📊 Expectancy neutral. Analiza antes de escalar.`;
    }

    getWorstPairReason(pair) {
        if (!pair) return 'Sin datos suficientes';
        if (pair.count < 5) return `⚠️ Muestra pequeña (${pair.count} trades). Espera más datos.`;
        if (pair.expectancy < -50) return `❌ Expectancy muy negativa ($${pair.expectancy.toFixed(0)}/trade). Evita este par.`;
        if (pair.expectancy < -20) return `❌ Expectancy negativa ($${pair.expectancy.toFixed(0)}/trade). Reduce exposición.`;
        if (pair.expectancy < 0) return `⚠️ Expectancy negativa ($${pair.expectancy.toFixed(0)}/trade). Toma un break.`;
        return `📊 Expectancy neutral. Revisa antes de operar.`;
    }

    isCurrentHourOptimal(bestHourTime) {
        // bestHourTime es string en formato "HH:00" (hora redonda)
        const now = new Date();
        const currentHour = now.getHours();
        
        const [bestHour] = bestHourTime.split(':').map(Number);
        
        // Ahora comparamos si estamos en la hora redonda correcta
        // Si bestHour es 09:00, consideramos óptimo entre 09:00 y 09:59
        return currentHour === bestHour;
    }

    getStreakPattern(metrics) {
        const streak = metrics.currentStreak;
        if (streak.consecutive >= 5) {
            return `${streak.consecutive} ${streak.type === 'WIN' ? 'victorias' : 'pérdidas'} consecutivas. Patrón fuerte detectado.`;
        }
        if (streak.consecutive >= 3) {
            return `${streak.consecutive} ${streak.type === 'WIN' ? 'victorias' : 'pérdidas'} consecutivas. Momentum significativo.`;
        }
        if (streak.consecutive > 0) {
            return `${streak.consecutive} ${streak.type === 'WIN' ? 'victoria' : 'pérdida'} ${streak.type === 'WIN' ? 'reciente' : 'reciente'}. Monitorea el siguiente trade.`;
        }
        return 'Trades alternando entre ganancias y pérdidas. Variabilidad normal.';
    }

    getStreakRecommendation(metrics) {
        const streak = metrics.currentStreak;
        const longestWin = metrics.allDayStreaks.longestWinStreak;
        const longestLoss = metrics.allDayStreaks.longestLossStreak;
        
        if (streak.type === 'WIN') {
            if (streak.consecutive >= longestWin && longestWin >= 5) {
                return `🔥 Estás en TU MEJOR RACHA del día (${streak.consecutive}/${longestWin}). Mantén la disciplina, cierra posiciones en profit y NO increases riesgo aún. Próximo objetivo: confirmar si es patrón o suerte.`;
            }
            if (streak.consecutive >= 3) {
                return `✅ Racha positiva (${streak.consecutive} consecutivos). Sigue tu setup confirmado pero prepárate para cambios. Tu máx hoy fue ${longestWin} - mantén ese ritmo sin obsesionarte.`;
            }
            return `✓ Último trade ganador. Excelente, pero el siguiente es independiente. Mantén tu plan de risk management y no cambies de setup por confianza.`;
        } else {
            if (streak.consecutive >= 3) {
                return `🛑 TOMA UN BREAK AHORA. Has perdido ${streak.consecutive} consecutivos (máx del día fue ${longestLoss}). Tu mentalidad está comprometida. Sal del gráfico por 30-60 min, respira, y vuelve si el setup es PERFECTO.`;
            }
            if (streak.consecutive === 2) {
                return `⚠️ Dos pérdidas seguidas. Analiza rápido: ¿mismo error? ¿cambió el mercado? Si es patrón, descansa. Si es coincidencia, ejecuta el siguiente setup con MÁXIMA disciplina.`;
            }
            return `📊 Último trade perdedor. Normal. Revisa: entrada tardía, stop mal colocado, o mercado contrario. Ajusta Y LUEGO vuelve. No operes por venganza.`;
        }
    }

    getKeyInsights(metrics) {
        const insights = [];
        const dayStats = this.analyzeDayPatterns(metrics.analysisTradesData, metrics.initialBalance);
        const todayStats = dayStats[metrics.dayName];

        // Insight 1: General performance
        if (metrics.winRate > 60) {
            insights.push(`Tu win rate del ${metrics.winRate.toFixed(0)}% es excelente. Esta es tu zona de confort.`);
        } else if (metrics.winRate < 40) {
            insights.push(`Tu win rate del ${metrics.winRate.toFixed(0)}% está bajo. Necesitas revisar tu estrategia de entrada.`);
        } else {
            insights.push(`Tu win rate del ${metrics.winRate.toFixed(0)}% es moderado. Busca setups de más alta probabilidad.`);
        }

        // Insight 2: Profit factor
        if (metrics.profitFactor > 1.5) {
            insights.push(`Profit Factor de ${metrics.profitFactor.toFixed(2)} es muy bueno. Tus ganancias superan pérdidas significativamente.`);
        } else if (metrics.profitFactor > 1) {
            insights.push(`Profit Factor de ${metrics.profitFactor.toFixed(2)} es saludable. Mantén esta relación.`);
        } else {
            insights.push(`Profit Factor bajo. Necesitas mejorar tu gestión de riesgo o selectividad de entradas.`);
        }

        // Insight 3: Best pair recommendation
        if (metrics.bestPair) {
            if (metrics.bestPair.count < 5) {
                insights.push(`${metrics.bestPair.pair} muestra potencial (${metrics.bestPair.count} trades). Espera más datos para confirmar.`);
            } else {
                insights.push(`${metrics.bestPair.pair} tiene expectancy de $${metrics.bestPair.expectancy.toFixed(0)}/trade. Aumenta volumen aquí.`);
            }
        }

        // Insight 4: Volume
        if (metrics.analysisTrades < 5) {
            insights.push(`Menos de 5 trades analizados. Necesitas más data para análisis estadístico confiable.`);
        } else if (metrics.analysisTrades > 20) {
            insights.push(`${metrics.analysisTrades} trades analizados. Excelente volumen de data para decisiones confiables.`);
        }

        // Insight 5: Day-specific analysis
        if (todayStats && todayStats.total > 0) {
            if (todayStats.winRate > metrics.winRate) {
                insights.push(`Los ${metrics.dayName}s tienes mejor desempeño (${todayStats.winRate.toFixed(0)}% vs ${metrics.winRate.toFixed(0)}%). Hoy es un buen día para operar.`);
            } else if (todayStats.winRate < 40) {
                insights.push(`Los ${metrics.dayName}s tienen bajo desempeño (${todayStats.winRate.toFixed(0)}%). Considera ser más conservador hoy.`);
            }
        }

        // Insight 8: Time analysis
        if (metrics.bestHour) {
            if (metrics.bestHour.count < 5) {
                insights.push(`Horario ${metrics.bestHour.time} muestra potencial (${metrics.bestHour.count} trades). Espera más data para confirmar.`);
            } else {
                insights.push(`Tu mejor horario es ${metrics.bestHour.time} con expectancy de $${metrics.bestHour.expectancy.toFixed(0)}/trade. Concentra tus setups aquí.`);
            }
        }

        return insights;
    }

    /**
     * Analiza patrones por día de la semana
     */
    analyzeDayPatterns(trades, initialBalance) {
        const daysOfWeek = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        const dayStats = {};

        // Inicializar estadísticas por día
        daysOfWeek.forEach(day => {
            dayStats[day] = {
                trades: [],
                total: 0,
                wins: 0,
                losses: 0,
                profit: 0,
                winRate: 0,
                maxStreak: 0,
                currentStreak: 0
            };
        });

        // Agrupar trades por día (usando zona horaria de España: CET/CEST)
        trades.forEach(t => {
            const timestamp = t.timestamp || t.created_at || '';
            const dateString = t.date || t.trade_date || '';
            
            let dayOfWeek = null;
            
            // PRIORIDAD 1: Usar dateString (YYYY-MM-DD) que es independiente de zona horaria
            if (dateString && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
                // Crear una fecha con el string YYYY-MM-DD interpretado como medianoche CET
                const [year, month, day] = dateString.split('-');
                const dateObj = new Date(year, parseInt(month) - 1, parseInt(day));
                dayOfWeek = daysOfWeek[dateObj.getDay()];
            }
            // PRIORIDAD 2: Si no hay dateString, usar timestamp pero convertido a zona horaria España
            else if (timestamp) {
                const dateObj = new Date(timestamp);
                // Convertir UTC a zona horaria de España (CET = UTC+1, CEST = UTC+2)
                const spainFormatter = new Intl.DateTimeFormat('es-ES', {
                    timeZone: 'Europe/Madrid',
                    weekday: 'long'
                });
                const dayName = spainFormatter.format(dateObj);
                // Mapear nombre del día español al array
                const dayNameMap = {
                    'domingo': 0,
                    'lunes': 1,
                    'martes': 2,
                    'miércoles': 3,
                    'jueves': 4,
                    'viernes': 5,
                    'sábado': 6
                };
                const dayIndex = dayNameMap[dayName.toLowerCase()];
                if (dayIndex !== undefined) {
                    dayOfWeek = daysOfWeek[dayIndex];
                }
            }
            
            if (!dayOfWeek || !dayStats[dayOfWeek]) return;

            const profit = parseFloat(t.profitUSD) || 0;
            dayStats[dayOfWeek].trades.push(t);
            dayStats[dayOfWeek].total++;
            dayStats[dayOfWeek].profit += profit;

            if (profit > 0) {
                dayStats[dayOfWeek].wins++;
            } else if (profit < 0) {
                dayStats[dayOfWeek].losses++;
            }
        });

        // Calcular estadísticas adicionales
        Object.values(dayStats).forEach(stats => {
            if (stats.total > 0) {
                // Excluir BE trades del Win Rate
                const beCount = stats.trades.filter(t => isBreakEvenTrade(t, initialBalance)).length;
                const totalExcludingBE = stats.total - beCount;
                const winsExcludingBE = stats.trades.filter(t => {
                    const profit = parseFloat(t.profitUSD) || 0;
                    return !isBreakEvenTrade(t, initialBalance) && profit > 0;
                }).length;
                
                stats.winRate = totalExcludingBE > 0 ? (winsExcludingBE / totalExcludingBE) * 100 : 0;
                
                // Calcular racha máxima
                let streak = 0;
                let maxStreak = 0;
                stats.trades.forEach(t => {
                    const profit = parseFloat(t.profitUSD) || 0;
                    if (profit > 0) {
                        streak++;
                        maxStreak = Math.max(maxStreak, streak);
                    } else {
                        streak = 0;
                    }
                });
                stats.maxStreak = maxStreak;
            }
        });

        return dayStats;
    }

    /**
     * Analiza ratios de Risk:Reward por día - NUEVA VERSIÓN
     * Calcula el ratio individual de cada trade ganador basado en % del balance inicial
     * 1:1 = ganancia del 1% del balance
     * 1:2 = ganancia del 2% del balance
     * 1:3 = ganancia del 3% del balance
     * 1:4+ = ganancia >= 4% del balance
     */
    analyzeRatiosByDay(trades, initialBalance = 10000) {
        const daysOfWeek = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        const ratioStats = {};

        // Inicializar
        daysOfWeek.forEach(day => {
            ratioStats[day] = {
                trades: [],
                winTrades: [],
                totalWins: 0,
                totalLosses: 0,
                totalProfit: 0,  // NUEVO: ganancia total del día
                // Buckets de ratios basados en % del balance
                ratio_1_1: 0,    // ganancia <= 1% del balance
                ratio_1_2: 0,    // 1% < ganancia <= 2%
                ratio_1_3: 0,    // 2% < ganancia <= 3%
                ratio_1_4plus: 0, // ganancia > 3%
                count: 0
            };
        });

        // Agrupar trades por día (usando zona horaria de España: CET/CEST)
        trades.forEach((t, idx) => {
            const timestamp = t.timestamp || t.created_at || '';
            const dateString = t.date || t.trade_date || '';
            
            let dayOfWeek = null;
            
            // PRIORIDAD 1: Usar dateString (YYYY-MM-DD) que es independiente de zona horaria
            if (dateString && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
                // Crear una fecha con el string YYYY-MM-DD interpretado como medianoche CET
                const [year, month, day] = dateString.split('-');
                const dateObj = new Date(year, parseInt(month) - 1, parseInt(day));
                dayOfWeek = daysOfWeek[dateObj.getDay()];
                if (idx < 3) {}
            }
            // PRIORIDAD 2: Si no hay dateString, usar timestamp pero convertido a zona horaria España
            else if (timestamp) {
                const dateObj = new Date(timestamp);
                // Convertir UTC a zona horaria de España (CET = UTC+1, CEST = UTC+2)
                const spainFormatter = new Intl.DateTimeFormat('es-ES', {
                    timeZone: 'Europe/Madrid',
                    weekday: 'long'
                });
                const dayName = spainFormatter.format(dateObj);
                if (idx < 3) {}
                
                // Mapear nombre del día español al array
                const dayNameMap = {
                    'domingo': 0,
                    'lunes': 1,
                    'martes': 2,
                    'miércoles': 3,
                    'jueves': 4,
                    'viernes': 5,
                    'sábado': 6
                };
                const dayIndex = dayNameMap[dayName.toLowerCase()];
                if (dayIndex !== undefined) {
                    dayOfWeek = daysOfWeek[dayIndex];
                }
            }
            
            if (!dayOfWeek || !ratioStats[dayOfWeek]) return;

            const profit = parseFloat(t.profitUSD) || 0;
            
            ratioStats[dayOfWeek].trades.push(t);
            ratioStats[dayOfWeek].count++;
            ratioStats[dayOfWeek].totalProfit += profit;  // NUEVO: acumular ganancia total

            if (profit > 0) {
                ratioStats[dayOfWeek].winTrades.push(profit);
                ratioStats[dayOfWeek].totalWins++;
                
                // Calcular ratio: ganancia como % del balance inicial
                const profitPct = (profit / initialBalance) * 100;
                
                if (idx < 10) {}
                
                // Clasificar en bucket
                if (profitPct <= 1) {
                    ratioStats[dayOfWeek].ratio_1_1++;
                } else if (profitPct <= 2) {
                    ratioStats[dayOfWeek].ratio_1_2++;
                } else if (profitPct <= 3) {
                    ratioStats[dayOfWeek].ratio_1_3++;
                } else {
                    ratioStats[dayOfWeek].ratio_1_4plus++;
                }
            } else if (profit < 0) {
                ratioStats[dayOfWeek].totalLosses++;
            }
        });

        return ratioStats;
    }

    /**
     * Genera SVG de tendencia por día de la semana
     */
    generateWeekTrendSVG(dayStats) {
        const daysOfWeek = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        const width = 280;
        const height = 100;
        const padding = 10;
        const barWidth = 35;
        const barSpacing = 5;

        // Obtener max win rate para escala
        const maxWR = Math.max(...Object.values(dayStats).map(s => s.winRate || 0), 50);
        const scale = (height - padding * 2) / maxWR;

        let svg = `<svg width="${width}" height="${height}" style="background: transparent;">`;

        daysOfWeek.forEach((day, idx) => {
            const stats = dayStats[day];
            const winRate = stats.winRate || 0;
            const barHeight = winRate * scale;
            const x = padding + idx * (barWidth + barSpacing);
            const y = height - padding - barHeight;
            
            // Reducir opacidad si pocos trades (menos de 3)
            const opacity = stats.total >= 3 ? 0.8 : 0.4;
            
            // Color según performance
            let color = '#64748b'; // gris
            if (winRate >= 60) color = '#10b981'; // verde
            else if (winRate >= 50) color = '#3b82f6'; // azul
            else if (winRate > 0) color = '#f59e0b'; // amber
            
            svg += `
                <!-- ${day} (${stats.total} trades) -->
                <rect x="${x}" y="${y}" width="${barWidth}" height="${barHeight}" fill="${color}" rx="2" opacity="${opacity}"/>
                <text x="${x + barWidth/2}" y="${height - 2}" text-anchor="middle" font-size="9" fill="#9ca3af" font-weight="bold">
                    ${day.substring(0, 3)}
                </text>
                <text x="${x + barWidth/2}" y="${y - 3}" text-anchor="middle" font-size="8" fill="#e5e7eb" font-weight="bold">
                    ${winRate > 0 ? Math.round(winRate) + '%' : '-'}
                </text>
                <!-- Indicador de volumen bajo -->
                ${stats.total < 3 ? `<text x="${x + barWidth/2}" y="${height - 8}" text-anchor="middle" font-size="7" fill="#f97316" font-weight="bold">⚠</text>` : ''}
            `;
        });

        svg += `</svg>`;
        return svg;
    }

    /**
     * Genera tabla HTML con estadísticas por día
     */
    generateDayStatsTable(dayStats) {
        const daysOfWeek = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        
        let html = `
            <div class="text-xs overflow-x-auto">
                <table class="w-full border-collapse">
                    <thead>
                        <tr class="border-b border-slate-600">
                            <th class="text-left py-1 px-2 text-gray-400">Día</th>
                            <th class="text-center py-1 px-2 text-gray-400">Trades</th>
                            <th class="text-center py-1 px-2 text-gray-400">WR</th>
                            <th class="text-right py-1 px-2 text-gray-400">Ganancia</th>
                            <th class="text-center py-1 px-2 text-gray-400">Racha Max</th>
                        </tr>
                    </thead>
                    <tbody>
        `;

        let totalTrades = 0;
        let totalProfit = 0;

        daysOfWeek.forEach(day => {
            const stats = dayStats[day];
            const profitColor = stats.profit >= 0 ? 'text-emerald-400' : 'text-red-400';
            totalTrades += stats.total;
            totalProfit += stats.profit;
            
            html += `
                <tr class="border-b border-slate-700/50 hover:bg-slate-800/30">
                    <td class="py-1 px-2 font-bold text-gray-300">${day.substring(0, 3)}</td>
                    <td class="text-center py-1 px-2 text-gray-400">${stats.total || '-'}</td>
                    <td class="text-center py-1 px-2 ${stats.winRate >= 50 ? 'text-emerald-400 font-bold' : stats.winRate > 0 ? 'text-amber-400' : 'text-gray-400'}">
                        ${stats.total > 0 ? Math.round(stats.winRate) + '%' : '-'}
                    </td>
                    <td class="text-right py-1 px-2 ${profitColor} font-bold">
                        ${stats.profit >= 0 ? '+' : ''}$${stats.profit.toFixed(0)}
                    </td>
                    <td class="text-center py-1 px-2">
                        ${stats.maxStreak > 0 ? `<span class="text-blue-400 font-bold">${stats.maxStreak} ${stats.maxStreak >= 3 ? '🔥' : ''}</span>` : '-'}
                    </td>
                </tr>
            `;
        });

        const totalProfitColor = totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400';
        html += `
                    <tr class="border-t border-slate-600 font-bold bg-slate-800/30">
                        <td class="py-2 px-2 text-gray-300">TOTAL</td>
                        <td class="text-center py-2 px-2 text-white">${totalTrades}</td>
                        <td class="text-center py-2 px-2">—</td>
                        <td class="text-right py-2 px-2 ${totalProfitColor}">${totalProfit >= 0 ? '+' : ''}$${totalProfit.toFixed(0)}</td>
                        <td class="text-center py-2 px-2">—</td>
                    </tr>
                    </tbody>
                </table>
            </div>
        `;

        return html;
    }

    /**
     * Genera tabla de ratios Risk:Reward por día - NUEVA VERSIÓN
     * Muestra distribución de ratios en buckets
     */
    generateRatiosTable(ratioStats) {
        const daysOfWeek = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        
        let html = `
            <div class="text-xs overflow-x-auto">
                <table class="w-full border-collapse">
                    <thead>
                        <tr class="border-b border-slate-600">
                            <th class="text-left py-1 px-2 text-gray-400">Día</th>
                            <th class="text-center py-1 px-2 text-gray-400">Trades</th>
                            <th class="text-center py-1 px-2 text-gray-400">W/L</th>
                            <th class="text-center py-1 px-2 text-gray-400">Ganancia</th>
                            <th class="text-center py-1 px-2 text-gray-400">1:1 (%)</th>
                            <th class="text-center py-1 px-2 text-gray-400">1:2 (%)</th>
                            <th class="text-center py-1 px-2 text-gray-400">1:3 (%)</th>
                            <th class="text-center py-1 px-2 text-gray-400">1:4+ (%)</th>
                        </tr>
                    </thead>
                    <tbody>
        `;

        let totalTrades = 0;
        let totalWins = 0;
        let totalLosses = 0;
        let totalProfit = 0;
        let total_1_1 = 0;
        let total_1_2 = 0;
        let total_1_3 = 0;
        let total_1_4plus = 0;

        daysOfWeek.forEach(day => {
            const stats = ratioStats[day];
            totalTrades += stats.count;
            totalWins += stats.totalWins;
            totalLosses += stats.totalLosses;
            totalProfit += stats.totalProfit;
            total_1_1 += stats.ratio_1_1;
            total_1_2 += stats.ratio_1_2;
            total_1_3 += stats.ratio_1_3;
            total_1_4plus += stats.ratio_1_4plus;
            
            // Calcular porcentajes sobre wins
            const pct_1_1 = stats.totalWins > 0 ? Math.round((stats.ratio_1_1 / stats.totalWins) * 100) : 0;
            const pct_1_2 = stats.totalWins > 0 ? Math.round((stats.ratio_1_2 / stats.totalWins) * 100) : 0;
            const pct_1_3 = stats.totalWins > 0 ? Math.round((stats.ratio_1_3 / stats.totalWins) * 100) : 0;
            const pct_1_4plus = stats.totalWins > 0 ? Math.round((stats.ratio_1_4plus / stats.totalWins) * 100) : 0;
            
            // Color de ganancia
            const profitColor = stats.totalProfit > 0 ? 'text-emerald-400 font-bold' : stats.totalProfit < 0 ? 'text-red-400 font-bold' : 'text-gray-400';
            
            html += `
                <tr class="border-b border-slate-700/50 hover:bg-slate-800/30">
                    <td class="py-1 px-2 font-bold text-gray-300">${day.substring(0, 3)}</td>
                    <td class="text-center py-1 px-2 text-gray-400">${stats.count || '-'}</td>
                    <td class="text-center py-1 px-2 text-gray-400" title="Wins/Losses">${stats.totalWins}/${stats.totalLosses}</td>
                    <td class="text-center py-1 px-2 ${profitColor}">$${stats.totalProfit.toFixed(0)}</td>
                    <td class="text-center py-1 px-2 ${pct_1_1 > 0 ? 'text-yellow-400 font-bold' : 'text-gray-500'}">${pct_1_1}%</td>
                    <td class="text-center py-1 px-2 ${pct_1_2 > 0 ? 'text-blue-400 font-bold' : 'text-gray-500'}">${pct_1_2}%</td>
                    <td class="text-center py-1 px-2 ${pct_1_3 > 0 ? 'text-green-400 font-bold' : 'text-gray-500'}">${pct_1_3}%</td>
                    <td class="text-center py-1 px-2 ${pct_1_4plus > 0 ? 'text-emerald-400 font-bold' : 'text-gray-500'}">${pct_1_4plus}%</td>
                </tr>
            `;
        });

        // Fila de totales
        const total_pct_1_1 = totalWins > 0 ? Math.round((total_1_1 / totalWins) * 100) : 0;
        const total_pct_1_2 = totalWins > 0 ? Math.round((total_1_2 / totalWins) * 100) : 0;
        const total_pct_1_3 = totalWins > 0 ? Math.round((total_1_3 / totalWins) * 100) : 0;
        const total_pct_1_4plus = totalWins > 0 ? Math.round((total_1_4plus / totalWins) * 100) : 0;
        
        const totalProfitColor = totalProfit > 0 ? 'text-emerald-400' : totalProfit < 0 ? 'text-red-400' : 'text-gray-400';

        html += `
                    <tr class="border-t border-slate-600 font-bold bg-slate-800/30">
                        <td class="py-2 px-2 text-gray-300">TOTAL</td>
                        <td class="text-center py-2 px-2 text-white">${totalTrades}</td>
                        <td class="text-center py-2 px-2 text-white">${totalWins}/${totalLosses}</td>
                        <td class="text-center py-2 px-2 ${totalProfitColor}">$${totalProfit.toFixed(0)}</td>
                        <td class="text-center py-2 px-2 text-yellow-400 font-bold">${total_pct_1_1}%</td>
                        <td class="text-center py-2 px-2 text-blue-400 font-bold">${total_pct_1_2}%</td>
                        <td class="text-center py-2 px-2 text-green-400 font-bold">${total_pct_1_3}%</td>
                        <td class="text-center py-2 px-2 text-emerald-400 font-bold">${total_pct_1_4plus}%</td>
                    </tr>
                    </tbody>
                </table>
            </div>
        `;

        return html;
    }

    /**
     * Genera SVG mostrando racha por día
     */
    generateStreakBarsChart(dayStats) {
        const daysOfWeek = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
        const width = 280;
        const height = 80;
        const padding = 10;
        const barWidth = 35;
        const barSpacing = 5;

        // Obtener max streak
        const maxStreak = Math.max(...Object.values(dayStats).map(s => s.maxStreak || 0), 3);
        const scale = (height - padding * 2) / maxStreak;

        let svg = `<svg width="${width}" height="${height}" style="background: transparent;">`;

        daysOfWeek.forEach((day, idx) => {
            const stats = dayStats[day];
            const streak = stats.maxStreak || 0;
            const barHeight = streak * scale;
            const x = padding + idx * (barWidth + barSpacing);
            const y = height - padding - barHeight;
            
            // Color según racha
            let color = '#64748b'; // gris
            if (streak >= 5) color = '#8b5cf6'; // purple
            else if (streak >= 3) color = '#06b6d4'; // cyan
            else if (streak > 0) color = '#3b82f6'; // blue
            
            svg += `
                <rect x="${x}" y="${y}" width="${barWidth}" height="${barHeight}" fill="${color}" rx="2" opacity="0.7"/>
                <text x="${x + barWidth/2}" y="${height - 2}" text-anchor="middle" font-size="9" fill="#9ca3af" font-weight="bold">
                    ${day.substring(0, 2)}
                </text>
                ${streak > 0 ? `<text x="${x + barWidth/2}" y="${y - 3}" text-anchor="middle" font-size="8" fill="#e5e7eb" font-weight="bold">${streak}</text>` : ''}
            `;
        });

        svg += `</svg>`;
        return svg;
    }
}

// Inicializar cuando el DOM esté listo
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
        window.iaScannerSAAS = new IAScannerSAAS();
    });
} else {
    window.iaScannerSAAS = new IAScannerSAAS();
}
